"use client";

// AuthOnboarding — Login + Registrierung + Erst-Workspace-Onboarding mit einer
// live laufenden Suite-Animation rechts.
//
// Faithful Port des ux-lab-Prototyps (janeway-translate/app/ux-lab/reel/page.tsx):
// gleiches Layout, gleicher Wizard (Design → Workspace → Konto), gleiche
// Reel-Animation, gleiche Styles. Zwei Dinge sind real statt Klickdummy:
//   1. Login/Registrierung rufen echtes Supabase-Auth (Muster aus LoginPage.tsx).
//   2. Theme-/Modus-Wechsel laufen ueber die zentrale runThemeTransition statt
//      einer lokalen View-Transition-Implementierung.
//
// ARCHITEKTUR-ENTSCHEIDUNG (aus dem Original uebernommen): EINE Uhr fuer alles.
// Die Reel-Uhr (im Reel-Player weiter unten) zaehlt Millisekunden, jedes Widget
// leitet daraus seinen lokalen Fortschritt p (0..1) ab. Kein Widget hat einen
// eigenen Timer — das Reel bleibt dadurch spulbar (Klick auf einen Strich
// springt in die Szene, alles steht sofort richtig).

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Building2, Rocket, Briefcase, Sparkles, Sun, Moon, Calendar, FileText, Mail,
  CheckSquare, Receipt, PenTool, TrendingUp, Phone, ScanText, ShieldAlert,
  BookMarked, Contact, HardDrive, Lock, ShieldCheck, Zap, MessageSquare, Lightbulb,
  GraduationCap, Repeat, Clock3, Workflow as WorkflowIcon,
  Ban, Blend, Radar, Slash, ArrowUp, ArrowLeft, ArrowRight, Check, ImagePlus, ChevronDown, Paperclip, Globe,
  type LucideIcon,
} from "lucide-react";
import { Input } from "../../ui/Input";
import { PasswordInput } from "../../ui/PasswordInput";
import { Button } from "../../ui/Button";
import { Label } from "../../ui/Label";
import { Checkbox } from "../../ui/Checkbox";
import { themes, getTheme } from "../../../lib/themes";
import { runThemeTransition, THEME_TRANSITIONS, type ThemeTransition } from "../../../lib/theme-transition";
import { THEME_COOKIE, COLOR_THEME_COOKIE, writeThemeCookie } from "../../../lib/theme-cookie";
import { basePath, isSafeRedirect } from "../../../lib/utils";
import { centralLegalUrl } from "@janeway/app-sdk/public-env";
import { KuendigungWiderrufLinks } from "../../legal/KuendigungWiderrufLinks";
import { janewayUrl } from "../../../lib/apps";
import { AppWordmark } from "../../layout/AppWordmark";
import { LoginAltcha, authBaseUrl, meldeSpamschutzUebersprungen, verifySuiteAltcha, type AltchaErgebnis } from "../LoginAltcha";
import { ZweitfaktorKarte } from "../../settings/ZweitfaktorKarte";
import { appFaktorenAus, passkeyFehlerText, passkeysAus } from "../../settings/passkey";
import { AuthSplitScreen, AuthShowcaseBackdrop } from "./AuthSplitScreen";
import { AuthCallback } from "./AuthCallback";
import { ThemeToggleButton } from "../../layout/TopNavActions";
import { useEffectiveThemeMode } from "../../../providers/ColorThemeProvider";
import { useAbmelden } from "../../../hooks/useAbmelden";
import {
  WCalendar, WChips, WDoc, WMail, WScan, WSpark, WStepper, WTable, WTasks,
  WWave, WConfirm, WChat, WGap, WAsk, WWorkflow, clamp01,
} from "./reel-widgets";

// Gleiche Icons wie der Design-Tab in gui-elements (TRANSITION_ICONS):
// Ban = sofort, Blend = weich, Radar = Ausbreitung, Slash = schraege Kante.
const TRANSITION_ICONS: Record<string, LucideIcon> = {
  none: Ban, soft: Blend, wave: Radar, diagonal: Slash,
};

const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

const ICONS = [
  { id: "building", label: "Gebäude", Icon: Building2 },
  { id: "rocket", label: "Rakete", Icon: Rocket },
  { id: "briefcase", label: "Koffer", Icon: Briefcase },
  { id: "sparkles", label: "Funken", Icon: Sparkles },
];

const CURATED = ["janeway", "claude", "supabase", "teal", "amber-minimal", "rose"];

// ── Szenen ─────────────────────────────────────────────────────────────
// Beats werden automatisch ausgelegt: erster Widget-Auftritt bei 2500 ms
// (nach Tippen + Denken), danach alle 850 ms der naechste.

type Beat = { at: number; dur: number; node: (p: number) => React.ReactNode };
type Node = {
  node: (p: number) => React.ReactNode;
  /** Eigene Laufzeit, z. B. laenger fuer das Freigabe-Tor. */
  dur?: number;
  /** Abstand bis zum NAECHSTEN Beat. Beim Freigabe-Tor gross, damit das
   *  Warten echt ist: der naechste Schritt darf erst nach der Freigabe kommen. */
  after?: number;
};

function lay(nodes: Node[], startAt = 2500): { beats: Beat[]; duration: number } {
  const GAP = 850;
  const DUR = 1800;
  const HOLD = 4200;
  let t = startAt;
  const beats = nodes.map((n) => {
    const b = { at: t, dur: n.dur ?? DUR, node: n.node };
    t += n.after ?? GAP;
    return b;
  });
  const last = beats[beats.length - 1];
  return { beats, duration: Math.max(t, last.at + last.dur) + HOLD };
}

// Diskriminierte Union: eine Auto-Szene hat KEINEN Prompt (das ist ja der
// Punkt), eine Prompt-/Freigabe-Szene keinen Ausloeser. Der Typ erzwingt das,
// statt es dem Zufall zu ueberlassen.
type Scene = {
  id: string;
  label: string;
  /** Leit-App der Szene — Eintrag in der App-Leiste unten. */
  lead: { Icon: LucideIcon; app: string };
  beats: Beat[];
  duration: number;
} & (
  | { mode: "prompt" | "confirm"; prompt: string; trigger?: undefined }
  | {
      mode: "auto" | "proactive" | "ask" | "suggest";
      trigger: { source: string; rule: string };
      prompt?: undefined;
    }
);

const SCENES: Scene[] = [
  {
    id: "meeting",
    lead: { Icon: Calendar, app: "Kalender" },
    label: "Kundengespräch",
    mode: "prompt" as const,
    prompt: "Bereite das Kundengespräch mit Talmann vor",
    ...lay([
      {
        node: (p) => (
          <WCalendar
            p={p} app="Kalender" Icon={Calendar} title="Do — Talmann GmbH"
            day={4} slot="14:00 – 15:00 · Raum 2 reserviert"
          />
        ),
      },
      {
        node: (p) => (
          <WDoc
            p={p} app="Notizen" Icon={FileText} title="Briefing Talmann GmbH"
            lines={["Umsatz +18 % seit Q2", "Offen: Rabattstaffel ab 50 Stk.", "Letzter Kontakt: 12.06."]}
          />
        ),
      },
      {
        node: (p) => (
          <WMail
            p={p} app="Mail" Icon={Mail} title="Entwurf: Agenda zum Termin"
            to="t.talmann@talmann-gmbh.example"
            body="Guten Tag Herr Talmann, anbei die Agenda für Donnerstag."
          />
        ),
      },
      {
        node: (p) => (
          <WTasks
            p={p} app="Aufgaben" Icon={CheckSquare} title="3 Aufgaben angelegt"
            items={["Zahlen prüfen · bis Mi", "Angebot anpassen · bis Do", "Raum bestätigen"]}
          />
        ),
      },
    ]),
  },
  {
    id: "offer",
    lead: { Icon: FileText, app: "Notizen" },
    label: "Angebot",
    mode: "prompt" as const,
    prompt: "Erstell ein Angebot über 12 Lizenzen für Rewald",
    ...lay([
      {
        node: (p) => (
          <WTable
            p={p} app="Notizen" Icon={FileText} title="Angebot AN-2026-0412"
            rows={[
              { label: "Suite Business", qty: "12 ×", value: 8280 },
              { label: "Onboarding", qty: "1 ×", value: 1200 },
              { label: "Support Premium", qty: "12 Mon.", value: 2160 },
            ]}
          />
        ),
      },
      {
        node: (p) => (
          <WStepper
            p={p} app="Mail" Icon={Mail} title="Angebot an Rewald verschickt"
            steps={["Erstellt", "Gesendet", "Geöffnet", "Angenommen"]}
          />
        ),
      },
      {
        node: (p) => (
          <WSpark
            p={p} app="Kontakte" Icon={TrendingUp} title="Pipeline nach Abschluss"
            points={[38, 44, 41, 58, 62, 71, 96]} value={11640} unit="€" caption="+21 % ggü. Vormonat"
          />
        ),
      },
    ]),
  },
  {
    id: "call",
    lead: { Icon: Contact, app: "Kontakte" },
    label: "Anruf",
    mode: "prompt" as const,
    prompt: "Ruf Talmann an und frag den Vertragsstand ab",
    ...lay([
      {
        node: (p) => (
          <WWave
            p={p} app="Anruf" Icon={Phone} title="Anruf läuft · 01:12"
            lines={[
              { who: "KI", text: "Guten Tag, kurze Frage zum Vertragsstand." },
              { who: "Talmann", text: "Liegt bei der Rechtsabteilung, bis Freitag." },
            ]}
          />
        ),
      },
      {
        node: (p) => (
          <WChips
            p={p} app="Erkannt" Icon={Sparkles} title="Aus dem Gespräch entnommen"
            chips={["Frist: Freitag", "Rechtsabteilung", "Kein Einwand zum Preis"]} tone="mark"
          />
        ),
      },
      {
        node: (p) => (
          <WTasks
            p={p} app="Aufgaben" Icon={CheckSquare} title="Wiedervorlage gesetzt"
            items={["Freitag 10:00 nachfassen", "Notiz im Kontakt ergänzt"]}
          />
        ),
      },
    ]),
  },
  {
    id: "contract",
    lead: { Icon: HardDrive, app: "Drive" },
    label: "Vertrag",
    mode: "prompt" as const,
    prompt: "Prüf den Vertrag — worauf muss ich achten?",
    ...lay([
      {
        node: (p) => (
          <WScan
            p={p} app="Drive" Icon={ScanText} title="Rahmenvertrag_Rewald.pdf · 14 Seiten"
            rows={[
              { text: "§2 Laufzeit: 24 Monate, Verlängerung automatisch" },
              { text: "§4 Kündigungsfrist: 3 Monate zum Laufzeitende", mark: true },
              { text: "§7 Preisanpassung einseitig möglich", mark: true },
              { text: "§9 Gerichtsstand: Hamburg" },
            ]}
          />
        ),
      },
      {
        node: (p) => (
          <WChips
            p={p} app="Risiken" Icon={ShieldAlert} title="2 Punkte brauchen dich"
            chips={["Einseitige Preisanpassung", "Auto-Verlängerung"]} tone="mark"
          />
        ),
      },
      {
        node: (p) => (
          <WCalendar
            p={p} app="Kalender" Icon={Calendar} title="Frist vorgemerkt"
            day={6} slot="Kündigungsfrist endet · Erinnerung 4 Wochen vorher"
          />
        ),
      },
    ]),
  },
  {
    id: "invoice",
    lead: { Icon: CheckSquare, app: "Aufgaben" },
    label: "Rechnung",
    mode: "auto" as const,
    trigger: { source: "Mail von buchhaltung@hartwig-technik.example", rule: "Regel: Eingehende Rechnung" },
    ...lay(
      [
        {
          node: (p) => (
            <WScan
              p={p} app="Drive" Icon={ScanText} title="RE-2026-8841.pdf erkannt"
              rows={[
                { text: "Rechnungsbetrag: 3.480,00 €", mark: true },
                { text: "Fällig: 12.08.2026", mark: true },
                { text: "Skonto: 2 % bis 29.07.2026", mark: true },
                { text: "Leistung: Wartung Q3" },
              ]}
            />
          ),
        },
        {
          node: (p) => (
            <WTable
              p={p} app="Beleg" Icon={Receipt} title="Automatisch gebucht"
              rows={[
                { label: "Wartung Q3", qty: "1 ×", value: 2924 },
                { label: "USt. 19 %", qty: "", value: 556 },
              ]}
            />
          ),
        },
        {
          node: (p) => (
            <WChips
              p={p} app="Zugeordnet" Icon={Sparkles} title="Ohne Rückfrage erledigt"
              chips={["Kostenstelle Technik", "Lieferant Hartwig", "Skonto-Frist gemerkt"]}
            />
          ),
        },
      ],
      1500,
    ),
  },
  {
    id: "approval",
    lead: { Icon: Mail, app: "Mail" },
    label: "Freigabe",
    mode: "confirm" as const,
    prompt: "Schick Rewald das überarbeitete Angebot",
    ...lay([
      {
        node: (p) => (
          <WDoc
            p={p} app="Notizen" Icon={FileText} title="Änderungen übernommen"
            lines={["Rabatt 8 % ab 50 Stück", "Laufzeit auf 12 Monate gekürzt", "Summe: 10.704,00 €"]}
          />
        ),
      },
      {
        node: (p) => (
          <WMail
            p={p} app="Mail" Icon={Mail} title="Entwurf steht bereit"
            to="einkauf@rewald-gmbh.example"
            body="Guten Tag, anbei das überarbeitete Angebot wie besprochen."
          />
        ),
      },
      {
        dur: 2600,
        after: 3200,
        node: (p) => (
          <WConfirm
            p={p} app="Freigabe" Icon={ShieldCheck} title="Jane wartet auf dich"
            summary="Angebot über 10.704,00 € an Rewald GmbH — Versand erst nach deiner Freigabe."
            at="freigegeben 14:32"
          />
        ),
      },
      {
        node: (p) => (
          <WStepper
            p={p} app="Zustellung" Icon={PenTool} title="Nach Freigabe verschickt"
            steps={["Freigegeben", "Gesendet", "Zugestellt", "Geöffnet"]}
          />
        ),
      },
    ]),
  },
  {
    id: "digest",
    lead: { Icon: MessageSquare, app: "Messenger" },
    label: "Wochenrückblick",
    mode: "proactive" as const,
    trigger: { source: "Jane meldet sich · Freitag 17:30", rule: "Von sich aus — niemand hat gefragt" },
    ...lay(
      [
        {
          node: (p) => (
            <WChat
              p={p} app="Messenger" Icon={MessageSquare} title="Jane schreibt dir"
              channel="WhatsApp · über Janeway Messenger"
              bubbles={[
                { from: "jane", text: "Kurzer Rückblick auf die Woche — nichts liegen geblieben." },
                { from: "jane", text: "3 Angebote raus, 1 Kündigungsfrist gerettet.", stat: "14,2 Std. übernommen" },
                { from: "you", text: "Danke 👍" },
              ]}
            />
          ),
        },
        {
          node: (p) => (
            <WSpark
              p={p} app="Auswertung" Icon={TrendingUp} title="Deine Woche"
              points={[42, 51, 47, 66, 72, 81, 94]} value={14.2} unit="Std." caption="Mo – Fr"
            />
          ),
        },
        {
          node: (p) => (
            <WChips
              p={p} app="Vorschläge" Icon={Lightbulb} title="Fürs nächste Mal"
              chips={["Dienstags weniger Termine", "Hartwig zahlt spät"]}
            />
          ),
        },
      ],
      1500,
    ),
  },
  {
    id: "gap",
    lead: { Icon: Sparkles, app: "Chat" },
    label: "Rückfrage",
    mode: "ask" as const,
    trigger: { source: "Jane fehlt eine Information", rule: "Erkannt über 3 Vorfälle in 9 Tagen" },
    ...lay(
      [
        {
          node: (p) => (
            <WGap
              p={p} app="Selbstprüfung" Icon={GraduationCap} title="Das konnte ich nicht beantworten"
              question="Wie lange dauert eine Sonderanfertigung?"
              hits={[
                { Icon: Phone, where: "Anruf · Rewald GmbH", when: "10.07." },
                { Icon: MessageSquare, where: "Chat · Website-Widget", when: "14.07." },
                { Icon: Phone, where: "Anruf · unbekannt", when: "18.07." },
              ]}
            />
          ),
        },
        {
          dur: 2600,
          after: 3000,
          node: (p) => (
            <WAsk
              p={p} app="Rückfrage" Icon={Sparkles} title="Jane fragt dich"
              question="Was soll ich künftig auf die Frage nach der Lieferzeit für Sonderanfertigungen antworten?"
              answer="6–8 Wochen ab Freigabe der Zeichnung, Eilzuschlag 4 Wochen."
              effect="Gelernt — ab sofort selbst beantwortet"
            />
          ),
        },
        {
          node: (p) => (
            <WChips
              p={p} app="Chat" Icon={BookMarked} title="Im Firmenwissen abgelegt"
              chips={["Lieferzeit Sonderanfertigung", "gilt für Telefon + Chat + Mail"]}
            />
          ),
        },
      ],
      1500,
    ),
  },
  {
    id: "automate",
    lead: { Icon: WorkflowIcon, app: "Automations" },
    label: "Automatisierung",
    mode: "suggest" as const,
    trigger: { source: "„Warte mal — das machst du doch jeden Montag.“", rule: "Muster erkannt über 4 Wochen" },
    ...lay(
      [
        {
          node: (p) => (
            <WDoc
              p={p} app="Muster" Icon={Repeat} title="4 Montage in Folge dasselbe"
              lines={["08:09 Zahlen aus Kontakte geholt", "08:12 Bericht in Notizen gebaut", "08:15 an das Team geschickt"]}
            />
          ),
        },
        {
          dur: 2600,
          after: 3200,
          node: (p) => (
            <WConfirm
              p={p} app="Vorschlag" Icon={Lightbulb} title="Soll ich das übernehmen?"
              summary="Ich könnte das ab Montag automatisch machen — du bekommst den Bericht fertig auf den Tisch."
              approveLabel="Automatisieren"
              at="übernommen 08:16"
            />
          ),
        },
        {
          dur: 3400,
          node: (p) => (
            <WWorkflow
              p={p} app="Automations" Icon={WorkflowIcon} title="Wochenbericht · gebaut und getestet"
              runtime="2,4 s"
              nodes={[
                { Icon: Clock3, label: "Jeden Montag 08:00", meta: "Auslöser" },
                { Icon: TrendingUp, label: "Zahlen aus Kontakte", meta: "Kontakte" },
                { Icon: FileText, label: "Bericht zusammenstellen", meta: "Notizen" },
                { Icon: Mail, label: "An das Team senden", meta: "Mail" },
              ]}
            />
          ),
        },
      ],
      1500,
    ),
  },
];

// Organische, aber DETERMINISTISCHE Tipp-Kadenz: Leerzeichen und Satzzeichen
// bekommen mehr Gewicht. Deterministisch, damit Spulen nicht flackert.
function typeSchedule(text: string, total: number): number[] {
  const w = [...text].map((ch, i) => {
    const r = Math.abs(Math.sin(i * 12.9898) * 43758.5453) % 1;
    if (ch === " ") return 2.0 + r * 1.3;
    if (",.!?—".includes(ch)) return 3.4 + r;
    return 0.7 + r * 0.95;
  });
  const sum = w.reduce((a, b) => a + b, 0);
  let acc = 0;
  return w.map((x) => ((acc += x) / sum) * total);
}

const TYPE_MS = 1500;
const THINK_FROM = 1650;
const THINK_TO = 2400;

function themeStyle(themeId: string, isDark: boolean): React.CSSProperties {
  const vars = getTheme(themeId)[isDark ? "dark" : "light"] as unknown as Record<string, string>;
  const out: Record<string, string> = {};
  for (const [k, val] of Object.entries(vars)) {
    out[`--${k}`] = val;
  }
  return out as React.CSSProperties;
}

/** Variablen des Ziel-Themes OHNE `--`-Praefix — runThemeTransition/der
 *  Klon-Reveal fuegen den Praefix selbst hinzu (setProperty(`--${k}`, v)). */
function nextThemeVars(themeId: string, dark: boolean): Record<string, string> {
  return getTheme(themeId)[dark ? "dark" : "light"] as unknown as Record<string, string>;
}

// ══════════════════════════════════════════════════════════════════════
// AuthOnboarding
// ══════════════════════════════════════════════════════════════════════

export interface AuthOnboardingProps {
  /** Wortmarken-Text, z. B. "Janeway Suite" (Hub/Auth) oder "Drive" (App). */
  appName: string;
  /** App-Glyph vor dem Schriftzug. false = nur Schriftzug (Hub/Auth). */
  showGlyph?: boolean;
  /** App-id fuer den AppIcon-Glyph-Lookup. */
  appId?: string;
  /** lucide-Glyph-Name als Fallback fuer neue Apps ohne Code-Eintrag. */
  glyphName?: string;
  /** Ziel nach erfolgreichem Login, wenn kein sicheres `?next=` vorliegt. */
  defaultRedirect?: string;
  /** E-Mail-Bestaetigungs-Callback (signUp emailRedirectTo). Default /auth/callback;
   *  die Auth-App nutzt /api/callback. */
  callbackPath?: string;
  /** Registrierung + Onboarding erlauben. false = reiner Login (z. B. admin). Default true. */
  allowRegister?: boolean;
  /**
   * Aus welchem Bereich registriert wird. Default: Suite.
   *
   * `"technology"` setzt allein janeway-hosting: die Registrierung soll dort KEINEN
   * Suite-Arbeitsbereich anlegen (Migrationen 723/724). Der Wert reist im Rumpf von
   * `POST /api/v1/register` mit; die Route entscheidet, nicht diese Komponente.
   *
   * Die Oberflaeche bleibt dieselbe — und das ist der Grund, es hier zu fuehren und
   * nicht in einer eigenen Maske je Bereich: an diesem Wizard haengen der
   * ALTCHA-Spamschutz, die AGB-/AVV-Zustimmung und der Empfehlungscode. Eine zweite
   * Maske haette alle drei verloren.
   */
  herkunft?: "suite" | "technology";
  /** Ziel des "Passwort vergessen?"-Links. Default /auth/reset-password (Auth-App: /reset-password). */
  resetPasswordHref?: string;
  /** Schritt, mit dem die Seite startet — z. B. "reset" fuer einen direkten
   *  Link auf die Passwort-vergessen-Ansicht. Default "login".
   *  "callback" ist kein Schritt, sondern die vierte Auth-Seite: der Rueckweg aus
   *  der Bestaetigungsmail (`/auth/callback`). Sie lag bis 2026-08-19 als 16
   *  Kopien in sechs Fassungen in den Apps, weil sie hier fehlte (R4-01 · S2).
   *  "registrieren" startet im ersten Registrierungsschritt ("design") — nicht zu
   *  verwechseln mit "einrichten", das ist die 2FA-Einrichtung. Ohne
   *  `allowRegister` faellt es auf "login" zurueck. */
  entry?: "login" | "mfa" | "einrichten" | "reset" | "update" | "callback" | "registrieren";
  /** Nur entry="callback": wohin bei Fehlern. Default `/auth/login`; admin liegt
   *  als einzige App auf `/login`. */
  loginHref?: string;
  /** "invite" = Eingeladenen-Onboarding: GENAU der Registrierungs-Wizard
   *  (Design → Workspace → Konto), nur startet er direkt beim Wizard und die
   *  E-Mail ist vorbelegt+gesperrt (das Konto existiert schon, der Invite-Link
   *  hat es bestaetigt + eingeloggt). Am Ende KEIN signUp/keine zweite
   *  Bestaetigungsmail, sondern — mit der aktiven Session — direkt: Passwort
   *  setzen (updateUser), eigenen Workspace anlegen, Theme in user_preferences
   *  persistieren, Invite einloesen (Team-Workspace), dann `welcomeRedirect`.
   *  Kein localStorage-Umweg (der ist origin-gebunden und faellt beim Wechsel
   *  auth.* → hub.* durch). Default "auth". */
  mode?: "auth" | "invite";
  /** invite-Modus: vorbelegte, gesperrte E-Mail (aus der aktiven Invite-Session). */
  presetEmail?: string;
  /** invite-Modus: einzuloesender Invite-Token — weist den Team-Workspace zu. */
  inviteToken?: string;
  /** Ziel nach Abschluss des invite-Onboardings (Hub-URL). Default "/". */
  welcomeRedirect?: string;
}

/** Was das Formular sieht: alles ausser `callback`, das vorher abgezweigt wird. */
type AuthOnboardingFormProps = Omit<AuthOnboardingProps, "entry"> & {
  entry?: "login" | "mfa" | "einrichten" | "reset" | "update" | "registrieren";
};

/**
 * Vier Auth-Seiten, ein Aufruf. `callback` wird VOR den Zustaenden abgezweigt:
 * der Rueckweg aus der Mail ist ein Spinner mit Weiterleitung, kein Formular —
 * er teilt sich mit dem Wizard keinen einzigen Zustand.
 */
export function AuthOnboarding({ entry = "login", ...rest }: AuthOnboardingProps) {
  if (entry === "callback") {
    return <AuthCallback defaultRedirect={rest.defaultRedirect} loginHref={rest.loginHref} />;
  }
  return <AuthOnboardingForm entry={entry} {...rest} />;
}

function AuthOnboardingForm({
  appName,
  showGlyph = false,
  appId,
  glyphName,
  defaultRedirect = "/",
  callbackPath = "/auth/callback",
  allowRegister = true,
  herkunft,
  resetPasswordHref = "/auth/reset-password",
  entry = "login",
  mode = "auth",
  presetEmail,
  inviteToken,
  welcomeRedirect = "/",
}: AuthOnboardingFormProps) {
  const [step, setStep] = useState<"login" | "mfa" | "einrichten" | "reset" | "update" | "design" | "workspace" | "register" | "jane" | "done">(
    mode === "invite" ? "design" : entry === "registrieren" ? (allowRegister ? "design" : "login") : entry,
  );
  const [dir, setDir] = useState<"fwd" | "back">("fwd");
  // `/logout` gibt es in keiner App (ST-39) — abgemeldet wird wie ueberall.
  const abmelden = useAbmelden();
  const [fullName, setFullName] = useState("");
  const [avatar, setAvatar] = useState<string | null>(null);
  const [name, setName] = useState("Mein Workspace");
  const [iconId, setIconId] = useState(ICONS[0].id);
  const [themeId, setThemeId] = useState("janeway");
  // Der Onboarding-Wizard laesst den Nutzer Farbschema UND Modus frei durchprobieren,
  // bevor irgendetwas gespeichert ist — dort fuehrt diese Seite eine EIGENE Achse und
  // startet bewusst dunkel. Die reinen Auth-Schritte (Login/Reset/Update) haben dafuer
  // keinen Grund: sie folgen dem, was <html> ohnehin schon traegt (SSR + Seed-Script
  // setzen es vor dem ersten Paint). Frueher galt die feste Dunkel-Achse fuer BEIDE
  // Faelle — deshalb blieb der Login-Screen dunkel, selbst wenn der Nutzer ueberall
  // sonst Hell eingestellt hatte, und ein Umschalter waere dort wirkungslos gewesen.
  const [wizardDark, setWizardDark] = useState(true);
  const [fx, setFx] = useState<ThemeTransition>("soft");

  // Echtes Auth (Muster aus LoginPage.tsx: createSupabaseBrowser + signIn/signUp).
  // Login und Registrierung teilen sich Email/Passwort — beide Schritte sind
  // nie gleichzeitig sichtbar, ein Nutzer durchlaeuft immer nur einen davon.
  const [email, setEmail] = useState(presetEmail ?? "");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(false);
  // Spamschutz (ALTCHA, Proof-of-Work auf eigener Infrastruktur) fuer Anmeldung,
  // Registrierung und Passwort-Reset — es gibt keinen Empfaenger; der Grund
  // steht im Kopf von `../LoginAltcha.tsx`.
  //
  // Ein Nachweis gilt genau EINMAL: der Server verbraucht die Challenge bei
  // der Einloesung. Nach jedem Versuch wird deshalb ueber `altchaReset` ein
  // frischer geholt.
  //
  // **Der Nachweis geht bewusst NICHT zusaetzlich an GoTrue.** GoTrue kennt nur
  // zwei fest einprogrammierte Captcha-Verfahren, ALTCHA ist keines davon — es
  // laesst sich dort gar nicht hinterlegen. Seit dem 2026-08-25 fuehrt die
  // Konfiguration deshalb ueberhaupt keine Captcha-Variable mehr (in beiden
  // Auth-Containern gemessen): die Erzwingung ist dort nicht abgeschaltet,
  // sondern nicht herstellbar.
  // **Der laufende Vorgang, nicht sein Ergebnis** — als Ref, damit `go()` ihn
  // nicht durch einen Neuaufbau verliert. Ein Zustandsfeld mit dem fertigen
  // Nachweis waere beim Absenden schlicht leer, solange die Rechnung noch laeuft;
  // das Gate liesse sich dann umgehen, indem man schnell genug klickt. Der
  // Aufrufer WARTET stattdessen (s. `spamschutzBestanden`).
  //
  // Der Grund eines Ausbleibens reist im Ergebnis mit. **Ihn zu verlieren war
  // schon einmal ein Aussperr-Vorfall:** bis gui-elements 0.181.0 warf jede
  // Einbindung ihn weg, schickte `null` an den Server, bekam 403 und brach ab,
  // BEVOR ueberhaupt ein Anmeldeversuch lief (gemessen 2026-08-11 auf
  // auth.janeway.io). Wer keinen Nachweis bekam, kam in KEINE App der Suite.
  const altchaLauf = useRef<Promise<AltchaErgebnis> | null>(null);
  const [altchaReset, setAltchaReset] = useState(0);
  // invite-Modus: Zustimmung zu AGB, Auftragsverarbeitungsvertrag und
  // Datenschutzerklaerung (ersetzt den frueheren separaten Consent-Screen).
  const [consent, setConsent] = useState(false);
  // Empfehlungs-/Aktionscode. Er kommt aus `?ref=` (die Affiliate-Weiterleitung
  // haengt ihn an das Ziel) und steht dann in einem SICHTBAREN, aenderbaren Feld.
  //
  // **Nichts davon wird auf dem Endgeraet gespeichert** — kein Cookie, kein
  // sessionStorage. Die Zuordnung dient dem Provisionsanspruch des Partners,
  // also einem Interesse des Betreibers und nicht einem vom Nutzer angeforderten
  // Dienst; § 25 Abs. 2 Nr. 2 TDDDG traegt das nicht, es waere nach Abs. 1
  // einwilligungspflichtig. Der Code reist in der Adresse mit und geht mit dem
  // `signUp` als `affiliate_code` in die Nutzer-Metadaten; der DB-Trigger
  // `handle_affiliate_referral_on_workspace` loest ihn dort auf.
  const [referralCode, setReferralCode] = useState("");
  // Passwort-Reset-Flow: teilt sich die obige E-Mail mit Login/Registrierung,
  // hat aber eigenen Erfolgs- und Neu-Passwort-Zustand.
  const [resetSent, setResetSent] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // `?ref=` einmal beim Aufsetzen in das sichtbare Feld heben. Die
  // Affiliate-Weiterleitung (`app-sdk/affiliate` → `/api/v1/affiliate/r/<code>`)
  // haengt den Code an ihr Ziel, und das Ziel ist die App-Wurzel — der Nutzer
  // landet also mit `?ref=` an der Anmeldung, nicht auf einer eigenen Seite.
  // `window.location` statt `useSearchParams`: die Suspense-Grenze, die der Hook
  // verlangt, gibt es hier nicht, und der Wert wird nur einmal gebraucht.
  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("ref")?.trim();
    if (code) setReferralCode(code);
  }, []);

  // Auf den Auth-Schritten fuehrt das Dokument den Modus, im Wizard diese Seite.
  // `useEffectiveThemeMode()` liefert beim SSR `undefined` — dort darf deshalb KEIN
  // modusabhaengiges Markup im Container stehen (kein `scoped`, keine `dark`-Klasse,
  // kein `isDark` an der Wortmarke). Genau so kommt der Modus rein aus <html>:
  // erster Paint korrekt, kein `mounted`-Gate, kein Aufblitzen.
  const dokumentModus = useEffectiveThemeMode();
  const fuehrtEigenenModus =
    step === "design" || step === "workspace" || step === "register" || step === "jane" || step === "done";
  const isDark = fuehrtEigenenModus ? wizardDark : dokumentModus === "dark";
  const setIsDark = setWizardDark;

  const scoped = useMemo(
    () => (fuehrtEigenenModus ? themeStyle(themeId, isDark) : undefined),
    [fuehrtEigenenModus, themeId, isDark],
  );
  const icon = ICONS.find((i) => i.id === iconId) ?? ICONS[0];

  // Onboarding-Wizard: Design → Workspace → Konto (Billing-Slot kommt spaeter).
  // Registrierung bewusst zuletzt (Endowment: erst gestalten/aufbauen, dann Konto).
  // Voller Wizard — auch invite: Design → Workspace → Konto (E-Mail vorbelegt).
  const ONB_STEPS = ["design", "workspace", "register"] as const;
  const onbIndex = ONB_STEPS.findIndex((s) => s === step);
  const onAvatar = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setAvatar(URL.createObjectURL(f));
  };

  // Schrittwechsel MIT Richtung — steuert die Gleit-Richtung der linken Spalte.
  // Loescht auch einen stehengebliebenen Auth-Fehler des vorherigen Schritts.
  //
  // Und den Spamschutz-Nachweis: jeder Schritt holt seinen eigenen. Ein
  // mitgeschleppter waere entweder schon verbraucht (er gilt genau einmal) oder
  // abgelaufen — beides faellt erst beim Absenden auf und sieht dann aus wie ein
  // kaputter Spamschutz statt wie ein alter Wert.
  const go = (next: typeof step, direction: "fwd" | "back" = "fwd") => {
    setDir(direction);
    setAuthError(null);
    altchaLauf.current = null;
    setStep(next);
  };

  /**
   * Zweitfaktor als SCHRITT dieses Formulars, nicht als eigene Seite.
   *
   * Die Middleware schickt eine `aal1`-Sitzung mit Faktor weiterhin auf `/mfa/verify`
   * — das ist die Auffanglinie fuer jeden, der eine geschuetzte Adresse direkt aufruft.
   * Wer sich HIER anmeldet, soll aber nicht mitten im Vorgang auf eine fremd wirkende
   * Seite geworfen werden: der zweite Faktor ist Teil der Anmeldung, also ist er ein
   * Schritt wie „Passwort vergessen" auch, in derselben Huelle.
   */
  const [mfaFaktorId, setMfaFaktorId] = useState<string | null>(null);
  /** Verifizierter Passkey des Kontos (GoTrue-Faktor `webauthn`); `null` ohne. */
  const [mfaPasskeyId, setMfaPasskeyId] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaZiel, setMfaZiel] = useState<string>("/");
  const [mfaAusweg, setMfaAusweg] = useState(false);
  const [mfaWiederherstellung, setMfaWiederherstellung] = useState("");
  const [mfaEntfernt, setMfaEntfernt] = useState(false);

  const spamschutzBestanden = async (): Promise<boolean> => {
    // Kein Nachweis: durchlassen — aber NICHT stillschweigend. Der Grund geht an
    // den Server und landet in der Spamschutz-Bilanz. Hier zu blocken hiesse,
    // jeden Nutzer aus jeder App der Suite auszusperren, sobald janeway-auth
    // ausfaellt; die Anmeldung selbst laeuft ueber GoTrue und haengt sonst nicht
    // an diesem Dienst.
    //
    // **Erst warten, dann urteilen.** `altchaLauf` ist das laufende Versprechen;
    // wer hier `await` weglaesst, liest bei einem schnellen Klick „kein Nachweis"
    // und laesst durch — das Gate waere durch blosse Eile zu umgehen.
    const { nachweis, fehler } = (await altchaLauf.current) ?? { nachweis: null, fehler: "netz" as const };
    if (!nachweis) {
      meldeSpamschutzUebersprungen(fehler ?? "kein-nachweis");
      return true;
    }
    if (await verifySuiteAltcha(nachweis)) return true;
    setAuthError("Spamschutz-Prüfung fehlgeschlagen. Bitte erneut versuchen.");
    setAltchaReset((n) => n + 1);
    return false;
  };

  /**
   * Wie `spamschutzBestanden`, aber **loest den Nachweis NICHT ein** — es gibt ihn
   * heraus.
   *
   * Warum es beides gibt: Ein Nachweis ist einmal gueltig. `verifySuiteAltcha`
   * loest ihn bei `/api/v1/altcha` ein; wer ihn danach an `/api/v1/register`
   * weiterreicht, bekommt dort die Wiedereinloesung als 403 zurueck. Fuer die
   * Registrierung prueft die Route selbst, also darf hier niemand vorgreifen.
   *
   * Anmelden und Zuruecksetzen benutzen weiter `spamschutzBestanden`: fuer sie
   * gibt es keine serverseitige Entsprechung, ihr Nachweis wird hier eingeloest
   * und ist fail-open — sonst sperrte ein Ausfall von janeway-auth jeden Nutzer
   * aus jeder App aus.
   */
  const spamschutzNachweisHolen = async (): Promise<string | null> => {
    const { nachweis, fehler } = (await altchaLauf.current) ?? { nachweis: null, fehler: "netz" as const };
    if (!nachweis) meldeSpamschutzUebersprungen(fehler ?? "kein-nachweis");
    return nachweis;
  };

  // Anmelden — mirrort LoginPage.tsx `handleLogin`: gleicher Supabase-Call,
  // gleiche Redirect-Logik (?next= nur wenn isSafeRedirect, sonst defaultRedirect).
  const handleLogin = async () => {
    setAuthError(null);
    setAuthLoading(true);
    try {
      // Spamschutz vor dem Anmeldeversuch — s. `spamschutzBestanden`.
      if (!(await spamschutzBestanden())) return;
      const { createSupabaseBrowser } = await import("@janeway/app-sdk/supabase");
      const supabase = createSupabaseBrowser();
      const { error: authErr } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (authErr) {
        setAuthError(authErr.message);
        setAltchaReset((n) => n + 1);
        return;
      }
      const redirectTo = new URLSearchParams(window.location.search).get("next");
      const target = redirectTo && isSafeRedirect(redirectTo) ? redirectTo : defaultRedirect;

      // Verlangt dieses Konto einen zweiten Faktor? `getAuthenticatorAssuranceLevel`
      // liest `aal` aus dem frischen Token und haelt es gegen die verifizierten
      // Faktoren — dasselbe, was die Middleware serverseitig entscheidet.
      const { data: stufe } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (stufe?.nextLevel === "aal2" && stufe.currentLevel !== "aal2") {
        const { data: faktoren } = await supabase.auth.mfa.listFactors();
        const faktor = appFaktorenAus(faktoren)[0];
        const passkey = passkeysAus(faktoren)[0];
        if (faktor || passkey) {
          setMfaFaktorId(faktor?.id ?? null);
          setMfaPasskeyId(passkey?.id ?? null);
          setMfaZiel(target);
          setAuthError(null);
          setMfaCode("");
          go("mfa", "fwd");
          return;
        }
      }

      window.location.href = target;
    } catch (err: unknown) {
      setAuthError(err instanceof Error ? err.message : "Anmeldung fehlgeschlagen. Bitte versuche es erneut.");
    } finally {
      setAuthLoading(false);
    }
  };

  /**
   * Einstieg DIREKT im Zweitfaktor-Schritt (`entry="mfa"`).
   *
   * Das ist der Weg, den die Middleware nimmt, wenn jemand eine geschuetzte Adresse
   * aufruft, ohne sich gerade angemeldet zu haben — Lesezeichen, neu geladene Seite,
   * zweiter Tab. Bis 2026-08-19 landete er dabei auf einer eigenen, einspaltigen
   * Seite mitten im Vorgang; jetzt ist es dieselbe Huelle wie die Anmeldung, nur mit
   * dem Zweitfaktor-Schritt vorn.
   */
  /**
   * Einstieg im EINRICHTUNGS-Schritt (`entry="einrichten"`).
   *
   * Bis 2026-08-20 war das eine eigene, einspaltige Seite. Sie fiel mitten im Ablauf
   * aus dem Layout — wer sich gerade registriert oder angemeldet hat, sieht sonst
   * zweimal etwas anderes und liest den Wechsel als Fehler. Jetzt ist es ein Schritt
   * dieses Assistenten, in derselben zweispaltigen Huelle wie Anmeldung und
   * Registrierung; die Route ist nur noch die Adresse.
   */
  useEffect(() => {
    if (entry !== "einrichten") return;
    const rueckweg = new URLSearchParams(window.location.search).get("next");
    setMfaZiel(rueckweg && isSafeRedirect(rueckweg) ? rueckweg : defaultRedirect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry]);

  useEffect(() => {
    if (entry !== "mfa" || mfaFaktorId || mfaPasskeyId) return;
    void (async () => {
      const rueckweg = new URLSearchParams(window.location.search).get("next");
      setMfaZiel(rueckweg && isSafeRedirect(rueckweg) ? rueckweg : defaultRedirect);
      const { createSupabaseBrowser } = await import("@janeway/app-sdk/supabase");
      const supabase = createSupabaseBrowser();
      // Die E-Mail fuellt das versteckte Kennungsfeld (s. u.) — ohne sie findet der
      // Passwortmanager seinen Eintrag nicht und bietet den Zeitcode nicht an.
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email) setEmail(user.email);
      const { data } = await supabase.auth.mfa.listFactors();
      const faktor = appFaktorenAus(data)[0];
      const passkey = passkeysAus(data)[0];
      if (faktor) setMfaFaktorId(faktor.id);
      if (passkey) setMfaPasskeyId(passkey.id);
      if (!faktor && !passkey) setAuthError("Für dieses Konto ist keine Zwei-Faktor-Anmeldung eingerichtet.");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry]);

  /** Code aus der Authenticator-App pruefen und weiterreisen. */
  const handleMfa = async () => {
    if (!mfaFaktorId) return;
    setAuthError(null);
    setAuthLoading(true);
    try {
      const { createSupabaseBrowser } = await import("@janeway/app-sdk/supabase");
      const supabase = createSupabaseBrowser();
      const { data: ch, error: chFehler } = await supabase.auth.mfa.challenge({ factorId: mfaFaktorId });
      if (chFehler || !ch) {
        setAuthError(chFehler?.message ?? "Prüfung fehlgeschlagen. Bitte erneut versuchen.");
        return;
      }
      const { error: vFehler } = await supabase.auth.mfa.verify({
        factorId: mfaFaktorId,
        challengeId: ch.id,
        code: mfaCode,
      });
      if (vFehler) {
        setAuthError("Der Code stimmt nicht. Er wechselt alle 30 Sekunden — nimm den aktuellen.");
        setMfaCode("");
        return;
      }
      window.location.href = mfaZiel;
    } finally {
      setAuthLoading(false);
    }
  };

  /**
   * Mit dem Passkey bestaetigen. `webauthn.authenticate` fuehrt Challenge, Browser-Zeremonie und
   * Verify in einem Zug aus; danach steht die Sitzung auf `aal2`, genau wie nach dem Code.
   * Abbrechen im Systemdialog ist kein Fehler des Kontos — der Code bleibt als Weg.
   */
  const handlePasskey = async () => {
    if (!mfaPasskeyId) return;
    setAuthError(null);
    setAuthLoading(true);
    try {
      const { createSupabaseBrowser } = await import("@janeway/app-sdk/supabase");
      const supabase = createSupabaseBrowser();
      const { error } = await supabase.auth.mfa.webauthn.authenticate({ factorId: mfaPasskeyId });
      if (error) {
        setAuthError(passkeyFehlerText(error));
        return;
      }
      window.location.href = mfaZiel;
    } catch (err) {
      setAuthError(passkeyFehlerText(err));
    } finally {
      setAuthLoading(false);
    }
  };

  /**
   * Wiederherstellungsschluessel einloesen. Er meldet NICHT an — er entfernt den zweiten
   * Faktor, danach geht es ueber die normale Anmeldung und eine Neueinrichtung.
   *
   * Die Route ist die der EIGENEN Herkunft: `corsHeaders` laesst Sitzungskeks ueber
   * Herkunftsgrenzen bewusst nicht zu, und jede App traegt die Durchreiche.
   */
  const handleWiederherstellung = async () => {
    setAuthError(null);
    setAuthLoading(true);
    try {
      const res = await fetch("/api/v1/zweitfaktor?aktion=einloesen", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ schluessel: mfaWiederherstellung }),
      });
      const j = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        setAuthError(j.error ?? "Schlüssel ungültig oder bereits verbraucht.");
        return;
      }
      setMfaEntfernt(true);
    } finally {
      setAuthLoading(false);
    }
  };

  // Konto erstellen — mirrort LoginPage.tsx `handleSignup`: gleicher
  // Supabase-Call (display_name/first_name/last_name in user_metadata,
  // emailRedirectTo auf /auth/callback). Bei Erfolg wird die Onboarding-Wahl
  // (Workspace-Name/Icon/Theme/Modus/Uebergang) fuer den naechsten echten
  // Schritt (Workspace-Anlage nach Bestaetigung) zwischengespeichert.
  const handleRegister = async () => {
    setAuthError(null);
    if (!consent) {
      setAuthError("Bitte stimme den AGB und dem Auftragsverarbeitungsvertrag zu.");
      return;
    }
    setAuthLoading(true);
    try {
      // Dieselbe Server-Pruefung wie beim Anmelden. Sie fehlte hier bis
      // gui-elements 0.181.0: der Nachweis ging nur an GoTrue, und GoTrue
      // prueft keinen — die Registrierung war in allen Apps serverseitig
      // ungeschuetzt.
      // **Der Nachweis geht MIT, er wird hier nicht eingeloest** — die Route
      // prueft ihn selbst und fail-closed. Wer ihn vorher bei `/api/v1/altcha`
      // einloest, bekommt dort die Wiedereinloesung als 403.
      const nachweis = await spamschutzNachweisHolen();
      const trimmedName = fullName.trim();
      const [firstName, ...rest] = trimmedName.split(" ");
      // Empfehlungscode aus dem sichtbaren Feld (aus `?ref=` oder von Hand
      // eingetippt). Der DB-Trigger `handle_affiliate_referral_on_workspace`
      // liest ihn aus `raw_user_meta_data`, loest ihn gegen einen aktiven
      // Partner auf und legt die Zuordnung an; kein Treffer = keine Zuordnung.
      const affiliateCode = referralCode.trim();
      // Seit gui-elements 0.348.0 laeuft die Registrierung ueber janeway-auth statt
      // direkt gegen GoTrue. Drei Gruende, und nur der erste war der Anlass:
      //   1. Der Spamschutz wird dort ERZWUNGEN. Im Browser war er eine Bitte —
      //      wer `supabase.auth.signUp` selbst aufrief, umging ihn vollstaendig.
      //   2. Die Route legt das `cortex:gateway`-Schluesselpaar des neuen
      //      Arbeitsbereichs an (`bootstrapWorkspace`). Der DB-Trigger kann das
      //      nicht — ihm fehlt `ENCRYPTION_KEY`. Selbstregistrierte Arbeitsbereiche
      //      waren deshalb angelegt, aber nicht benutzbar.
      //   3. Sie protokolliert die Zustimmung serverseitig (AGB § 1 Abs. 4).
      const antwort = await fetch(`${authBaseUrl()}/api/v1/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          first_name: firstName ?? "",
          last_name: rest.join(" "),
          ...(affiliateCode ? { affiliate_code: affiliateCode } : {}),
          ...(nachweis ? { altcha_nachweis: nachweis } : {}),
          accepted_terms: true,
          ...(herkunft === "technology" ? { herkunft } : {}),
          redirect_to: `${window.location.origin}${callbackPath}`,
        }),
      });
      if (!antwort.ok) {
        const leib = await antwort.json().catch(() => null);
        const meldung =
          typeof leib?.error === "string"
            ? leib.error
            : leib?.error?.message ?? "Registrierung fehlgeschlagen. Bitte versuche es erneut.";
        setAltchaReset((n) => n + 1);
        throw new Error(meldung);
      }
      try {
        window.localStorage.setItem(
          "janeway_onboarding",
          JSON.stringify({ workspaceName: name, iconId, themeId, isDark, fx }),
        );
      } catch {
        /* ignore — Onboarding-Zwischenspeicher ist ein Nice-to-have, kein Blocker */
      }
      go("done", "fwd");
    } catch (err: unknown) {
      setAuthError(err instanceof Error ? err.message : "Registrierung fehlgeschlagen. Bitte versuche es erneut.");
    } finally {
      setAuthLoading(false);
    }
  };

  // invite-Abschluss (Eingeladene, Konto existiert + ist via Invite-Link
  // authentifiziert): KEIN signUp / keine zweite Bestaetigungsmail. Alles laeuft
  // DIREKT mit der aktiven Session — sonst scheitert der localStorage-Umweg am
  // Origin-Wechsel auth.* → hub.* (dort ist der Stash nicht lesbar; genau das
  // war der Grund, dass das Theme "nicht geladen" wurde). Reihenfolge: Passwort
  // + Name setzen, eigenen Workspace anlegen, Theme in user_preferences (+Cookies)
  // persistieren, Team-Invite einloesen, dann in den Hub.
  const handleInviteComplete = async () => {
    setAuthError(null);
    if (password.length < 8) {
      setAuthError("Passwort muss mindestens 8 Zeichen lang sein.");
      return;
    }
    if (!consent) {
      setAuthError("Bitte stimme den AGB und dem Auftragsverarbeitungsvertrag zu.");
      return;
    }
    setAuthLoading(true);
    try {
      const { createSupabaseBrowser } = await import("@janeway/app-sdk/supabase");
      const supabase = createSupabaseBrowser();

      // Zwei GETRENNTE Namen (M363 entkoppelt den Personal-WS vom Profilnamen):
      //  - display_name = der eingegebene Nutzername (Profil).
      //  - der im Workspace-Schritt gewaehlte Name = Name des persoenlichen
      //    Workspaces (via Definer-RPC, nicht mehr an display_name gebunden).
      // KEIN separater Workspace -> genau Personal-WS (so benannt) + Team-WS = 2.
      // Fallback ist der eingegebene Nutzername, nicht "Mein Workspace": Der Schritt
      // benennt hier den PERSOENLICHEN Bereich (siehe Kommentar am workspace-Schritt),
      // und ein leer gelassenes Feld soll zu "Max Mustermann" fuehren, nicht zu einem
      // generischen Namen, den man spaeter nicht mehr zuordnen kann. Der DB-Trigger
      // benennt den Personal-WS ohnehin nach display_name (M383) — dieser Wert
      // ueberschreibt ihn nur, wenn der Nutzer bewusst etwas anderes eintraegt.
      const wsName = name.trim() || fullName.trim() || "Mein Bereich";
      const trimmedName = fullName.trim();
      const [firstName, ...rest] = trimmedName.split(" ");
      const { error: upErr } = await supabase.auth.updateUser({
        password,
        data: {
          display_name: trimmedName || wsName,
          first_name: firstName ?? "",
          last_name: rest.join(" "),
          accepted_terms_at: new Date().toISOString(),
        },
      });
      if (upErr) throw upErr;

      // Persoenlichen Workspace auf den gewaehlten Namen setzen (Definer-RPC, M363).
      try {
        await supabase.rpc("infra_set_personal_workspace_name", { p_name: wsName });
      } catch {
        /* Benennung best-effort — der Personal-WS existiert ohnehin (Trigger) */
      }

      // Theme DIREKT persistieren: Cookies (geraete-lokal) + user_preferences
      // (cross-origin/cross-device — das ist der Weg, ueber den der Hub das Theme
      // wirklich laedt). Kein localStorage.
      try {
        writeThemeCookie(COLOR_THEME_COOKIE, themeId);
        writeThemeCookie(THEME_COOKIE, isDark ? "dark" : "light");
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          await supabase.from("user_preferences").upsert(
            {
              user_id: session.user.id,
              color_theme_id: themeId,
              theme_overrides: { light: {}, dark: {} },
              dark_mode: isDark,
              updated_at: new Date().toISOString(),
            },
            { onConflict: "user_id" },
          );
        }
      } catch {
        /* Theme-Persistenz best-effort */
      }

      // Team-Einladung einloesen (weist den bei der Einladung gewaehlten
      // Workspace zu). Schlaegt es fehl, geht es trotzdem in den Hub — der
      // eigene Workspace steht ja.
      if (inviteToken) {
        try {
          await fetch("/api/v1/invites/redeem", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ token: inviteToken }),
          });
        } catch {
          /* Redeem best-effort */
        }
      }

      window.location.href = welcomeRedirect;
    } catch (err: unknown) {
      setAuthError(err instanceof Error ? err.message : "Einrichtung fehlgeschlagen. Bitte versuche es erneut.");
    } finally {
      setAuthLoading(false);
    }
  };

  // Reset-Link anfordern — mirrort reset-password/page.tsx: gleicher
  // Supabase-Call, Redirect-Ziel aus `callbackPath` + "?type=recovery".
  const handleResetRequest = async () => {
    setAuthError(null);
    setAuthLoading(true);
    try {
      // Dritter Auth-Weg, dieselbe Pruefung. Der Reset-Link verschickt eine Mail
      // an eine frei waehlbare Adresse — ohne Gate ist das ein Versandkanal.
      if (!(await spamschutzBestanden())) return;
      const { createSupabaseBrowser } = await import("@janeway/app-sdk/supabase");
      const supabase = createSupabaseBrowser();
      const { error: authErr } = await supabase.auth.resetPasswordForEmail(email, {
        // `callbackPath` statt eines festen "/auth/callback" — genau wie signUp es
        // oben schon macht. Die Prop existierte, wurde hier aber ignoriert, und
        // janeway-auth uebergibt "/api/callback", weil es KEIN app/auth/ hat. Der
        // Reset-Link zeigte dort also auf eine Route, die es nicht gibt: er landete
        // auf /login?next=/auth/callback, "?type=recovery" ging verloren und der Code
        // wurde nie eingeloest — "Passwort vergessen" war auf der zentralen Auth-App
        // damit funktionslos. /api/callback wertet type=recovery aus und leitet auf
        // /update-password weiter.
        redirectTo: `${window.location.origin}${basePath}${callbackPath}?type=recovery`,
      });
      if (authErr) {
        setAuthError(authErr.message);
        return;
      }
      setResetSent(true);
    } catch (err: unknown) {
      setAuthError(err instanceof Error ? err.message : "Anfrage fehlgeschlagen. Bitte versuche es erneut.");
    } finally {
      setAuthLoading(false);
    }
  };

  // Neues Passwort setzen — mirrort update-password/page.tsx: gleiche
  // Validierung (Uebereinstimmung + Mindestlaenge) vor dem Supabase-Call.
  const handleUpdatePassword = async () => {
    setAuthError(null);
    if (newPassword !== confirmPassword) {
      setAuthError("Passwörter stimmen nicht überein.");
      return;
    }
    if (newPassword.length < 8) {
      setAuthError("Passwort muss mindestens 8 Zeichen lang sein.");
      return;
    }
    setAuthLoading(true);
    try {
      const { createSupabaseBrowser } = await import("@janeway/app-sdk/supabase");
      const supabase = createSupabaseBrowser();
      const { error: authErr } = await supabase.auth.updateUser({ password: newPassword });
      if (authErr) {
        setAuthError(authErr.message);
        return;
      }
      // Ergebnis bewusst ungeprueft: das Passwort IST geaendert (wie in SicherheitTab).
      const { merkePasswortwechsel } = await import("@janeway/app-sdk/auth/sicherheitsstand-actions");
      await merkePasswortwechsel().catch(() => undefined);
      window.location.href = defaultRedirect;
    } catch (err: unknown) {
      setAuthError(err instanceof Error ? err.message : "Speichern fehlgeschlagen. Bitte versuche es erneut.");
    } finally {
      setAuthLoading(false);
    }
  };

  // Eine Farbschema-Kachel — genutzt fuer die 6 Favoriten UND die aufgeklappten.
  const renderThemeTile = (t: (typeof themes)[number]) => {
    const sv = isDark ? t.dark : t.light;
    const active = t.id === themeId;
    return (
      <button
        key={t.id}
        type="button"
        onClick={(ev) =>
          runThemeTransition(fx, () => setThemeId(t.id), ev.currentTarget, nextThemeVars(t.id, isDark), isDark)
        }
        aria-pressed={active}
        className={`rounded-[var(--radius)] border p-2 text-left transition-all ${
          active
            ? "border-primary bg-accent/30 ring-2 ring-primary/25"
            : "border-border hover:border-foreground/50"
        }`}
      >
        <span className="mb-1 flex gap-1">
          <span className="h-3 w-3 rounded-sm border border-black/10" style={{ background: sv.background }} />
          <span className="h-3 w-3 rounded-sm" style={{ background: sv.primary }} />
          <span className="h-3 w-3 rounded-sm" style={{ background: sv.accent }} />
        </span>
        <span className="block truncate text-xs font-medium">{t.label}</span>
      </button>
    );
  };

  return (
    <div
      style={scoped}
      className={`relative min-h-[var(--jw-nutzhoehe)] bg-background text-foreground ${
        fuehrtEigenenModus && isDark ? "dark" : ""
      } ${fx === "soft" ? "fx-soft" : ""}`}
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `
        /* "weich": nur Farb-Eigenschaften. Bewusst NICHT transform/opacity/width —
           die treiben die Reel-Animation und wuerden traege werden.
           !important, weil next-themes mit disableTransitionOnChange laeuft und
           beim Umschalten ein "* { transition: none !important }" einschiebt,
           das die Transition sonst sofort abbricht. */
        .fx-soft, .fx-soft * {
          transition: background-color 420ms ease, border-color 420ms ease,
                      color 420ms ease, fill 420ms ease, stroke 420ms ease !important;
        }
        /* Ausnahme: die Nav-Stationen animieren ihre BREITE (die Zunge faehrt
           raus/ein). Der Blanket oben kappt das (width fehlt bewusst, damit die
           Reel-Uhr nicht traege wird) → hier gezielt wieder erlauben, sonst
           SPRINGT der Stationswechsel im Default-Modus "weich". */
        .fx-soft [data-reel-station] {
          transition: width 520ms cubic-bezier(0.22, 1, 0.36, 1) !important;
        }
        /* Linke Spalte wechselt Schritt — RICHTUNGSABHAENGIG: vorwaerts gleitet
           der neue Inhalt von rechts herein, zurueck von links. Nur SIE animiert
           (key-Remount), das Reel rechts laeuft unveraendert weiter. */
        .auth-fwd  { animation: authInFwd 400ms cubic-bezier(0.22, 1, 0.36, 1) both; }
        .auth-back { animation: authInBack 400ms cubic-bezier(0.22, 1, 0.36, 1) both; }
        @keyframes authInFwd {
          from { opacity: 0; transform: translateX(26px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes authInBack {
          from { opacity: 0; transform: translateX(-26px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        /* Reset-Link versendet: der Inhalt gleitet sanft hoch, das Mail-Badge
           poppt herein, der Haken setzt sich mit kurzem Versatz obendrauf. */
        .reset-sent  { animation: resetSentIn 460ms cubic-bezier(0.22, 1, 0.36, 1) both; }
        .reset-badge { animation: resetBadgePop 520ms cubic-bezier(0.34, 1.56, 0.64, 1) both; }
        .reset-check { animation: resetCheckPop 380ms cubic-bezier(0.34, 1.56, 0.64, 1) 300ms both; }
        @keyframes resetSentIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes resetBadgePop {
          from { opacity: 0; transform: scale(0.6); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes resetCheckPop {
          from { opacity: 0; transform: scale(0); }
          to   { opacity: 1; transform: scale(1); }
        }
        /* Kopf-Fade: die scrollbare linke Spalte wird oben weich ausgeblendet,
           damit Inhalt nicht sichtbar hinter der Wortmarke durchscrollt.

           Bis 0.240.0 galt das nur ab 1024px, mit der Begruendung, mobil scrolle
           die Wortmarke mit dem Body weg. Das stimmte nie — unter 768px steht
           der Body fest (styles/globals.css), und seit die Spalte auf jeder
           Breite selbst scrollt (AuthSplitScreen), bleibt die Wortmarke stehen,
           waehrend der Text darunter durchlaeuft. Der Fade gilt deshalb ueberall.
           Achtung: dieser Block steht in einem Template-Literal — keine
           Backticks im Kommentar, sie beenden den String. */
        .auth-scroll-fade {
          -webkit-mask-image: linear-gradient(to bottom, transparent 0, transparent 3.25rem, #000 6rem);
                  mask-image: linear-gradient(to bottom, transparent 0, transparent 3.25rem, #000 6rem);
        }
        @media (prefers-reduced-motion: reduce) {
          .fx-soft, .fx-soft * { transition: none; }
          .auth-fwd, .auth-back { animation: none; }
          .reset-sent, .reset-badge, .reset-check { animation: none; }
        }
      `,
        }}
      />
      {/* Wortmarke ganz in die obere linke Ecke — wie der App-Header. showGlyph
          steuert, ob ein App-Icon davor sitzt (Hub/Auth: nur Schriftzug). */}
      <div className="absolute left-4 top-4 z-20 sm:left-5 sm:top-5 lg:left-6">
        <AppWordmark
          name={appName}
          showGlyph={showGlyph}
          appId={appId}
          glyphName={glyphName}
          // Nur im Wizard fuehren wir den Modus selbst. Sonst `undefined`, damit
          // AppIcon seinen CSS-Zweig nimmt — der stimmt beim ersten Paint und
          // erzeugt keine Abweichung zwischen Server- und Client-Markup.
          isDark={fuehrtEigenenModus ? isDark : undefined}
          animated
          className="[&>span:last-child]:text-[1.9rem] [&>span:last-child]:tracking-[-0.02em]"
        />
      </div>

      {/* Hell/Dunkel-Umschalter — spiegelbildlich zur Wortmarke. Nur auf den
          Auth-Schritten: im Wizard uebernimmt das Modus-Segment diese Rolle.
          Schreibt NICHT selbst am Cookie; setMode -> ThemeCookieSync erledigt das
          ueber writeThemeCookie(), das zuvor den host-only Doppelgaenger raeumt.
          min-h-11/min-w-11 = 44px Touch-Ziel (Default waeren 38px). */}
      {!fuehrtEigenenModus && (
        <div className="absolute right-4 top-4 z-20 sm:right-5 sm:top-5 lg:right-6">
          <ThemeToggleButton className="inline-flex min-h-11 min-w-11 items-center justify-center text-muted-foreground hover:text-foreground" />
        </div>
      )}

      <AuthSplitScreen
        links={
          <>
          <div
            key={step}
            className={`mx-auto my-auto w-full max-w-[23rem] ${dir === "back" ? "auth-back" : "auth-fwd"}`}
          >
            {step === "login" ? (
              <div className="space-y-3.5">
                <div className="space-y-1.5">
                  <h1 className="text-[1.7rem] font-semibold leading-[1.12] tracking-tight">
                    Willkommen zurück
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    Melde dich in deiner Janeway Suite an.
                  </p>
                </div>

                {/* Echtes <form>: Enter im E-Mail- oder Passwortfeld loest den CTA
                    aus. Vorher war jeder Knopf hier `type="button"` mit `onClick`,
                    ohne Formular drumherum — Enter tat schlicht nichts, und die
                    Anmeldung ging nur mit der Maus. */}
                <form
                  noValidate
                  onSubmit={(e) => { e.preventDefault(); void handleLogin(); }}
                  className="mt-7 space-y-3.5"
                >
                  <div className="space-y-1.5">
                    <Label htmlFor="rl-email">E-Mail</Label>
                    <div className="relative">
                      <Mail
                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                        size={15}
                        aria-hidden="true"
                      />
                      <Input
                        id="rl-email"
                        type="email"
                        autoComplete="email"
                        placeholder="hello@janeway.io"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="min-h-11 pl-9 text-base sm:text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="rl-pass">Passwort</Label>
                      <button
                        type="button"
                        onClick={() => go("reset", "fwd")}
                        className="text-xs text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground"
                      >
                        Passwort vergessen?
                      </button>
                    </div>
                    <PasswordInput
                      id="rl-pass"
                      icon={Lock}
                      autoComplete="current-password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="min-h-11 text-base sm:text-sm"
                    />
                  </div>

                  {authError && (
                    <p className="text-xs text-destructive">{authError}</p>
                  )}

                  {/* Spamschutz: unsichtbar, solange er rechtzeitig fertig wird.
                      Er rechnet schon, waehrend hier noch getippt wird. */}
                  <LoginAltcha
                    onLauf={(lauf) => { altchaLauf.current = lauf; }}
                    zweck="anmeldung"
                    resetSignal={altchaReset}
                  />

                  <Button
                    type="submit"
                    disabled={authLoading}
                    className="min-h-11 w-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
                  >
                    {authLoading ? "Anmelden..." : "Anmelden"}
                  </Button>
                </form>

                {allowRegister && (
                  <p className="mt-6 text-sm text-muted-foreground">
                    Noch kein Konto?{" "}
                    <button
                      type="button"
                      onClick={() => go("design", "fwd")}
                      className="font-medium text-foreground underline underline-offset-2 hover:no-underline"
                    >
                      Jetzt einrichten
                    </button>
                  </p>
                )}
              </div>
            ) : step === "einrichten" ? (
              // ── Zweiten Faktor einrichten — Schritt, nicht eigene Seite ────
              <div className="space-y-3.5">
                <div className="space-y-1.5">
                  <h1 className="text-[1.7rem] font-semibold leading-[1.12] tracking-tight">
                    Konto absichern
                  </h1>
                  {/* Allgemein gehalten: WARUM man hier ist (neu, Schluessel nachholen,
                      vom Support zurueckgesetzt), sagt die Karte darunter selbst. */}
                  <p className="text-sm text-muted-foreground">
                    Für dein Konto ist die Zwei-Faktor-Anmeldung vorgeschrieben. Du brauchst
                    dafür eine Authenticator-App oder einen Passwortmanager — welche, und wo
                    du tippst, zeigen wir dir gleich.
                  </p>
                </div>

                <div className="mt-7">
                  <ZweitfaktorKarte
                    erzwungen
                    onFertig={() => { window.location.href = mfaZiel; }}
                  />
                </div>

                {/* Der einzige Ausgang — eine Schranke ohne ihn macht Support-Faelle
                    statt Sicherheit. */}
                <div className="mt-6 border-t border-border pt-4">
                  <button
                    type="button"
                    onClick={() => { void abmelden(); }}
                    className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
                  >
                    Abmelden
                  </button>
                </div>
              </div>
            ) : step === "mfa" ? (
              // ── Zweiter Faktor — derselbe Rahmen wie die Anmeldung ────────
              mfaEntfernt ? (
                <div className="space-y-6">
                  <div className="space-y-1.5">
                    <h1 className="text-[1.7rem] font-semibold leading-[1.12] tracking-tight">
                      Zwei-Faktor-Anmeldung entfernt
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      Melde dich jetzt neu an und richte sie gleich wieder ein — bis dahin
                      schützt nur dein Passwort dein Konto.
                    </p>
                  </div>
                  <Button
                    onClick={() => { void abmelden(); }}
                    className="min-h-11 w-full bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    Neu anmelden
                  </Button>
                </div>
              ) : (
                <div className="space-y-3.5">
                  <div className="space-y-1.5">
                    <h1 className="text-[1.7rem] font-semibold leading-[1.12] tracking-tight">
                      {mfaAusweg ? "Kein Zugriff auf die App?" : "Noch ein Schritt"}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      {mfaAusweg
                        ? "Gib deinen Wiederherstellungsschlüssel ein."
                        : mfaPasskeyId && mfaFaktorId
                          ? "Bestätige mit deinem Passkey oder gib den Code aus deiner Authenticator-App ein."
                          : mfaPasskeyId
                            ? "Bestätige die Anmeldung mit deinem Passkey."
                            : "Gib den sechsstelligen Code aus deiner Authenticator-App ein."}
                    </p>
                  </div>

                  {mfaPasskeyId && !mfaAusweg ? (
                    <Button
                      type="button"
                      onClick={() => void handlePasskey()}
                      disabled={authLoading}
                      className="mt-7 min-h-11 w-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
                    >
                      {authLoading ? "Warte auf das Gerät …" : "Mit Passkey bestätigen"}
                    </Button>
                  ) : null}

                  <form
                    noValidate
                    onSubmit={(e) => {
                      e.preventDefault();
                      void (mfaAusweg ? handleWiederherstellung() : handleMfa());
                    }}
                    className={`mt-7 space-y-3.5 ${mfaPasskeyId && !mfaFaktorId && !mfaAusweg ? "hidden" : ""}`}
                  >
                    {/* **Hier steht KEINE E-Mail.** Betreiber-Entscheidung vom
                        2026-08-19: der Zweitfaktor-Schritt fragt einen Code, sonst
                        nichts.

                        Der Preis ist bekannt und bewusst bezahlt: Passwortmanager
                        ordnen ein Formular ueber die Kennung einem gespeicherten
                        Eintrag zu. Ohne sie bietet Proton Pass den hinterlegten
                        Zeitcode moeglicherweise nicht an — ein verstecktes Feld hilft
                        dabei NICHT, `sr-only` ist fuer den Detektor genauso unsichtbar
                        wie fuer den Nutzer (am 2026-08-19 erfolglos versucht).
                        Wer das wieder aufmacht, aendert eine Festlegung, keinen Fehler. */}
                    {mfaAusweg ? (
                      <div className="space-y-1.5">
                        <Label htmlFor="rl-wh">Wiederherstellungsschlüssel</Label>
                        <Input
                          id="rl-wh"
                          autoComplete="off"
                          placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX"
                          value={mfaWiederherstellung}
                          onChange={(e) => setMfaWiederherstellung(e.target.value.toUpperCase())}
                          className="min-h-11 text-base sm:text-sm"
                        />
                        <p className="text-[11px] text-muted-foreground">
                          Der Schlüssel meldet dich nicht an — er <strong>entfernt</strong> die
                          Zwei-Faktor-Anmeldung, damit du wieder hereinkommst. Er gilt genau einmal.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        <Label htmlFor="rl-mfa">Bestätigungscode</Label>
                        <div className="relative">
                          <ShieldCheck
                            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                            size={15}
                            aria-hidden="true"
                          />
                          <Input
                            id="rl-mfa"
                            /* **`name` ist der Teil, an dem Passwortmanager haengen.**
                               `autoComplete="one-time-code"` allein genuegt 1Password und
                               Bitwarden nicht — sie suchen zusaetzlich nach einem Feldnamen
                               aus dem ueblichen Vorrat (`otp`, `totp`, `code`). Ohne ihn
                               bietet der Manager den hinterlegten Zeitcode gar nicht an, und
                               man tippt ihn vom Telefon ab. Am 2026-08-19 gemeldet. */
                            name="otp"
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            autoComplete="one-time-code"
                            autoFocus
                            maxLength={6}
                            placeholder="123456"
                            value={mfaCode}
                            onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                            className="min-h-11 pl-9 text-base tracking-[0.4em] sm:text-sm"
                          />
                        </div>
                      </div>
                    )}

                    {authError && <p className="text-xs text-destructive">{authError}</p>}

                    <Button
                      type="submit"
                      // `!mfaFaktorId` gehoert dazu: beim Einstieg ueber die Adresse
                      // (`entry="mfa"`) wird der Faktor erst nachgeladen. Ohne diese
                      // Bedingung ist der Knopf in der ersten Sekunde bedienbar und tut
                      // nichts — `handleMfa` kehrt still zurueck. Genau so entsteht der
                      // Eindruck, die Seite habe nicht richtig geladen.
                      disabled={
                        authLoading ||
                        (mfaAusweg ? !mfaWiederherstellung : mfaCode.length < 6 || !mfaFaktorId)
                      }
                      className="min-h-11 w-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
                    >
                      {authLoading ? "Prüfen …" : mfaAusweg ? "Zwei-Faktor-Anmeldung entfernen" : "Bestätigen"}
                    </Button>
                  </form>

                  <button
                    type="button"
                    onClick={() => { setMfaAusweg(!mfaAusweg); setAuthError(null); }}
                    className="mt-6 text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground"
                  >
                    {mfaAusweg
                      ? mfaPasskeyId && !mfaFaktorId ? "Zurück zum Passkey" : "Zurück zur Code-Eingabe"
                      : mfaPasskeyId ? "Kein Zugriff auf deinen zweiten Faktor?" : "Kein Zugriff auf die Authenticator-App?"}
                  </button>
                </div>
              )
            ) : step === "reset" ? (
              resetSent ? (
                // ── Reset-Link versendet ──────────────────────────────────
                <div className="reset-sent space-y-6">
                  <div className="reset-badge relative flex h-14 w-14 items-center justify-center rounded-[var(--radius)] bg-primary">
                    <Mail size={24} className="text-primary-foreground" aria-hidden="true" />
                    <span className="reset-check absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground ring-2 ring-background">
                      <Check size={12} strokeWidth={3} aria-hidden="true" />
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    <h1 className="text-[1.7rem] font-semibold leading-[1.12] tracking-tight">
                      Postfach prüfen
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      Wir haben dir einen Link an{" "}
                      <span className="font-medium text-foreground">
                        {email.trim() || "deine E-Mail"}
                      </span>{" "}
                      geschickt. Öffne die Mail und folge dem Link, um ein neues Passwort zu vergeben.
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground/70">
                    Keine Mail erhalten? Sieh im Spam-Ordner nach oder{" "}
                    <button
                      type="button"
                      onClick={() => void handleResetRequest()}
                      disabled={authLoading}
                      className="font-medium text-foreground underline underline-offset-2 hover:no-underline disabled:opacity-50"
                    >
                      erneut senden
                    </button>
                    .
                  </p>
                  <button
                    type="button"
                    onClick={() => go("login", "back")}
                    className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <ArrowLeft size={14} aria-hidden="true" /> Zurück zum Login
                  </button>
                </div>
              ) : (
                <div className="space-y-3.5">
                  <div className="space-y-1.5">
                    <h1 className="text-[1.7rem] font-semibold leading-[1.12] tracking-tight">
                      Passwort zurücksetzen
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      Wir senden dir einen Link per E-Mail.
                    </p>
                  </div>

                  <form
                    noValidate
                    onSubmit={(e) => { e.preventDefault(); void handleResetRequest(); }}
                    className="mt-7 space-y-3.5"
                  >
                    <div className="space-y-1.5">
                      <Label htmlFor="rr-email">E-Mail</Label>
                      <div className="relative">
                        <Mail
                          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                          size={15}
                          aria-hidden="true"
                        />
                        <Input
                          id="rr-email"
                          type="email"
                          autoComplete="email"
                          placeholder="hello@janeway.io"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="min-h-11 pl-9 text-base sm:text-sm"
                        />
                      </div>
                    </div>

                    {authError && (
                      <p className="text-xs text-destructive">{authError}</p>
                    )}

                    {/* Spamschutz auch hier: bis 0.181.0 fehlte er an diesem Weg
                        vollstaendig — `handleResetRequest` schickte einen Token,
                        den nie jemand erzeugt hatte. Der Reset-Link verschickt
                        eine Mail an eine frei waehlbare Adresse; ohne Gate ist
                        das ein Versandkanal. */}
                    <LoginAltcha
                      onLauf={(lauf) => { altchaLauf.current = lauf; }}
                      zweck="anmeldung"
                      resetSignal={altchaReset}
                    />

                    <Button
                      type="submit"
                      disabled={authLoading}
                      className="min-h-11 w-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
                    >
                      {authLoading ? "Senden…" : "Link senden"}
                    </Button>
                  </form>

                  <button
                    type="button"
                    onClick={() => go("login", "back")}
                    className="mt-6 inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <ArrowLeft size={14} aria-hidden="true" /> Zurück zum Login
                  </button>
                </div>
              )
            ) : step === "update" ? (
              <div className="space-y-3.5">
                <div className="space-y-1.5">
                  <h1 className="text-[1.7rem] font-semibold leading-[1.12] tracking-tight">
                    Neues Passwort
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    Wähle ein sicheres Passwort (mind. 8 Zeichen).
                  </p>
                </div>

                <form
                  noValidate
                  onSubmit={(e) => { e.preventDefault(); void handleUpdatePassword(); }}
                  className="mt-7 space-y-3.5"
                >
                  <div className="space-y-1.5">
                    <Label htmlFor="ru-pass">Neues Passwort</Label>
                    <PasswordInput
                      id="ru-pass"
                      icon={Lock}
                      autoComplete="new-password"
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="min-h-11 text-base sm:text-sm"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="ru-confirm">Passwort bestätigen</Label>
                    <PasswordInput
                      id="ru-confirm"
                      icon={Lock}
                      autoComplete="new-password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="min-h-11 text-base sm:text-sm"
                    />
                  </div>

                  {authError && (
                    <p className="text-xs text-destructive">{authError}</p>
                  )}

                  <Button
                    type="submit"
                    disabled={authLoading}
                    className="min-h-11 w-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
                  >
                    {authLoading ? "Speichern…" : "Passwort speichern"}
                  </Button>
                </form>
              </div>
            ) : step === "done" ? (
              // ── Registriert — jetzt liegt der Ball beim Postfach ───────
              //
              // WAS HIER VORHER STAND, und warum es weg musste: „Alles bereit! Dein
              // Workspace steht. Jane kann loslegen." mit einem Knopf „Zur Suite".
              // Beides war falsch. `signUp` oben schickt eine Bestaetigungsmail
              // (`emailRedirectTo`) — es gibt an dieser Stelle noch KEINE Sitzung, und
              // der Knopf trug weder `onClick` noch `href`: er tat buchstaeblich nichts.
              // Wer der Ueberschrift glaubte, hat ihn wieder und wieder gedrueckt.
              //
              // Ein Bildschirm, der Erfolg meldet und dessen einzige Handlung ins Leere
              // greift, ist schlimmer als gar keiner: er laesst den Nutzer den Fehler bei
              // sich suchen. Deshalb steht hier jetzt genau das, was als Naechstes
              // passiert — und die Adresse, an die es geht.
              <div className="space-y-6">
                <div className="flex h-14 w-14 items-center justify-center rounded-[var(--radius)] bg-primary">
                  <Mail size={26} className="text-primary-foreground" aria-hidden="true" />
                </div>
                <div className="space-y-1.5">
                  <h1 className="text-[1.7rem] font-semibold leading-[1.12] tracking-tight">
                    Schau in dein Postfach.
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    Wir haben dir einen Bestätigungslink geschickt an:
                  </p>
                </div>

                {/* Die Adresse sichtbar, weil ein Vertipper sonst erst nach Tagen
                    auffaellt — und dann als „eure Mail kommt nicht an". */}
                <p className="break-all text-sm font-medium text-foreground">
                  {email || "deine Adresse"}
                </p>

                <div className="space-y-1.5 text-xs text-muted-foreground">
                  <p>
                    Klick den Link darin, um dein Konto zu bestätigen. Danach stehst du
                    auf der Liste, und wir melden uns, sobald du dran bist.
                  </p>
                  <p>
                    Nichts angekommen? Sieh im Spam-Ordner nach. Die erste Mail landet
                    dort erfahrungsgemäß am ehesten.
                  </p>
                </div>

                {/* KEIN „Zur Suite"-Knopf mehr: es gibt noch nichts, wohin er fuehren
                    koennte. Der einzige Weg von hier fuehrt ueber das Postfach — oder
                    zurueck, wenn die Adresse falsch war. */}
                <button
                  type="button"
                  onClick={() => go("register", "back")}
                  className="text-xs font-medium text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground"
                >
                  Andere Adresse verwenden
                </button>
              </div>
            ) : (
              // ── Onboarding-Wizard: Design → Workspace → Konto ──────────
              // Ein Formular ueber alle drei Schritte: der CTA ist derselbe Knopf,
              // also traegt auch Enter je nach Schritt „Weiter" oder „Konto
              // erstellen". Alle uebrigen Knoepfe hier sind `type="button"` —
              // ohne das wuerde ein Klick auf eine Theme-Kachel absenden.
              <form
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                  if (step === "design") go("workspace", "fwd");
                  else if (step === "workspace") go("register", "fwd");
                  else if (mode === "invite") void handleInviteComplete();
                  else void handleRegister();
                }}
                className="space-y-6"
              >
                {/* Kopf: Zurueck + Fortschritt (Goal-Gradient) */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    {mode === "invite" && step === "design" ? (
                      <span aria-hidden="true" />
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          go(step === "design" ? "login" : step === "workspace" ? "design" : "workspace", "back")
                        }
                        className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                      >
                        <ArrowLeft size={14} aria-hidden="true" /> Zurück
                      </button>
                    )}
                    <span className="font-mono text-[10px] tabular-nums text-muted-foreground">
                      Schritt {onbIndex + 1} von {ONB_STEPS.length}
                    </span>
                  </div>
                  <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{
                        width: `${((onbIndex + 1) / ONB_STEPS.length) * 100}%`,
                        transition: "width 500ms cubic-bezier(0.22, 1, 0.36, 1)",
                      }}
                    />
                  </div>
                </div>

                {step === "design" && (
                  <div className="space-y-5">
                    <div className="space-y-1.5">
                      <h1 className="text-[1.7rem] font-semibold leading-[1.12] tracking-tight">
                        Wähl deinen Look
                      </h1>
                      <p className="text-sm text-muted-foreground">
                        Farbschema und Übergang — rechts siehst du es sofort live.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Farbschema</Label>
                      <div className="grid grid-cols-3 gap-1.5">
                        {themes.filter((t) => CURATED.includes(t.id)).map(renderThemeTile)}
                      </div>
                      {/* 6 Favoriten sichtbar, der Rest klappt auf. */}
                      <details className="group">
                        <summary className="flex cursor-pointer list-none items-center gap-1 pt-0.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground [&::-webkit-details-marker]:hidden">
                          <ChevronDown
                            size={13}
                            className="transition-transform group-open:rotate-180"
                            aria-hidden="true"
                          />
                          Weitere Farbschemata
                        </summary>
                        <div className="mt-1.5 grid grid-cols-3 gap-1.5">
                          {themes.filter((t) => !CURATED.includes(t.id)).map(renderThemeTile)}
                        </div>
                        <p className="mt-2 text-[10px] leading-relaxed text-muted-foreground">
                          Ganz eigenes Design — Farben, Radius und Übergänge — erstellst du nach der
                          Anmeldung in den Einstellungen.
                        </p>
                      </details>
                    </div>

                    <div className="space-y-2">
                      <Label>Farbmodus</Label>
                      <div className="flex gap-0.5 rounded-[var(--radius)] border border-border bg-muted p-0.5">
                        {(
                          [
                            ["light", "Hell", Sun],
                            ["dark", "Dunkel", Moon],
                          ] as const
                        ).map(([mode, label, I]) => {
                          const nextDark = mode === "dark";
                          const active = nextDark === isDark;
                          return (
                            <button
                              key={mode}
                              type="button"
                              onClick={(ev) =>
                                runThemeTransition(
                                  fx,
                                  () => setIsDark(nextDark),
                                  ev.currentTarget,
                                  nextThemeVars(themeId, nextDark),
                                  nextDark,
                                )
                              }
                              aria-pressed={active}
                              className={`flex min-h-9 flex-1 items-center justify-center gap-1.5 rounded-[calc(var(--radius)-2px)] text-xs transition-colors ${
                                active ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                              }`}
                            >
                              <I size={12} aria-hidden="true" /> {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Übergang beim Wechsel</Label>
                      <div className="grid grid-cols-4 gap-1">
                        {THEME_TRANSITIONS.map((t) => {
                          const Icon = TRANSITION_ICONS[t.id];
                          const active = fx === t.id;
                          return (
                            <button
                              key={t.id}
                              type="button"
                              onClick={() => setFx(t.id)}
                              aria-pressed={active}
                              title={t.hint}
                              className={`flex flex-col items-center gap-1 rounded-md border py-2 text-[10px] transition-colors ${
                                active
                                  ? "border-primary bg-primary text-primary-foreground"
                                  : "border-border text-muted-foreground hover:text-foreground"
                              }`}
                            >
                              <Icon size={13} aria-hidden="true" />
                              {t.label}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-[10px] leading-snug text-muted-foreground">
                        {THEME_TRANSITIONS.find((t) => t.id === fx)?.hint ?? "Variante wählen, dann Farbschema oder Hell/Dunkel klicken."}
                      </p>
                    </div>

                  </div>
                )}

                {step === "workspace" && (
                  <div className="space-y-5">
                    {/* Im invite-Modus benennt dieser Schritt den PERSOENLICHEN Bereich,
                        nicht den Team-Bereich — der kommt aus der Einladung und hat
                        laengst einen Namen. Bis 2026-07-27 stand hier fuer beide Modi
                        "Wie heisst dein Workspace?" mit dem Platzhalter "z. B. Nordwind
                        Studio". Eingeladene lasen das als Firmenfrage und trugen ihren
                        Firmennamen ein — der landete per infra_set_personal_workspace_name
                        auf ihrem persoenlichen Workspace. Genau so sind in Prod die
                        Personal-Workspaces "Offix24" und "Enterprise X3" entstanden
                        (beide Personen sind zusaetzlich Mitglied im Team-Workspace
                        "Elektroprojekte"). Die Frage muss im invite-Modus also eindeutig
                        nach dem eigenen Bereich klingen, sonst waechst der Fehler nach. */}
                    <div className="space-y-1.5">
                      <h1 className="text-[1.7rem] font-semibold leading-[1.12] tracking-tight">
                        {mode === "invite" ? "Dein persönlicher Bereich" : "Dein Workspace"}
                      </h1>
                      <p className="text-sm text-muted-foreground">
                        {mode === "invite"
                          ? "Nur für dich — der Team-Bereich, in den du eingeladen wurdest, kommt zusätzlich dazu."
                          : "Name und Symbol — erscheint gleich rechts in deiner Suite."}
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="rl-name">
                        {mode === "invite"
                          ? "Wie soll dein persönlicher Bereich heißen?"
                          : "Wie heißt dein Workspace?"}
                      </Label>
                      <Input
                        id="rl-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={mode === "invite" ? (fullName.trim() || "z. B. dein Name") : "z. B. Nordwind Studio"}
                        className="min-h-11 text-base sm:text-sm"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Symbol</Label>
                      <div className="flex flex-wrap gap-2">
                        {ICONS.map(({ id, label, Icon }) => (
                          <button
                            key={id}
                            type="button"
                            onClick={() => setIconId(id)}
                            aria-label={label}
                            aria-pressed={id === iconId}
                            className={`flex h-11 w-11 items-center justify-center rounded-[var(--radius)] border transition-colors ${
                              id === iconId ? "border-primary bg-accent" : "border-border hover:bg-accent/50"
                            }`}
                          >
                            <Icon size={18} />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {step === "register" && (
                  <div className="space-y-5">
                    <div className="space-y-1.5">
                      <h1 className="text-[1.7rem] font-semibold leading-[1.12] tracking-tight">
                        {mode === "invite" ? "Fast geschafft" : "Fast fertig"}
                      </h1>
                      <p className="text-sm text-muted-foreground">
                        {mode === "invite"
                          ? "Leg dein Passwort fest — deine E-Mail ist bereits bestätigt."
                          : `Nur noch ein Konto — damit „${name.trim() || "dein Workspace"}" gespeichert bleibt.`}
                      </p>
                      {/* Erwartung VOR dem Absenden setzen, nicht danach. Wer erst auf
                          der Warteseite erfaehrt, dass er wartet, empfindet es als
                          Absage; wer es vorher liest, als Anmeldung zu etwas. Bei einer
                          Einladung entfaellt der Satz — der Eingeladene ist bereits
                          freigegeben und stuende sonst vor einer Liste, auf der er gar
                          nicht steht. */}
                      {mode !== "invite" && (
                        <p className="text-sm text-muted-foreground">
                          Wir öffnen Janeway schrittweise: Dein Konto sichert dir den
                          Platz, die Freigabe schicken wir per Mail.
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-3.5">
                      <label
                        className="group relative flex h-16 w-16 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-[var(--radius)] border border-dashed border-border bg-secondary/30 bg-cover bg-center transition-colors hover:border-foreground/60"
                        style={avatar ? { backgroundImage: `url(${avatar})` } : undefined}
                      >
                        {!avatar && (
                          <ImagePlus
                            size={20}
                            className="text-muted-foreground transition-colors group-hover:text-foreground"
                            aria-hidden="true"
                          />
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={onAvatar}
                          aria-label="Profilbild hochladen"
                        />
                      </label>
                      <div className="min-w-0">
                        <p className="text-sm font-medium">
                          {avatar ? "Sieht gut aus" : "Profilbild hinzufügen"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {avatar ? "Klick aufs Bild zum Ändern." : "Optional — gib deinem Konto ein Gesicht."}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="rg-name">Dein Name</Label>
                      <Input
                        id="rg-name"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        autoComplete="name"
                        placeholder="Vor- und Nachname"
                        className="min-h-11 text-base sm:text-sm"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="rg-email">E-Mail</Label>
                      <div className="relative">
                        <Mail
                          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                          size={15}
                          aria-hidden="true"
                        />
                        <Input
                          id="rg-email"
                          type="email"
                          autoComplete="email"
                          placeholder="hello@janeway.io"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          readOnly={mode === "invite"}
                          className={`min-h-11 pl-9 text-base sm:text-sm ${
                            mode === "invite" ? "cursor-not-allowed text-muted-foreground" : ""
                          }`}
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="rg-pass">Passwort</Label>
                      <PasswordInput
                        id="rg-pass"
                        icon={Lock}
                        autoComplete="new-password"
                        placeholder="Mindestens 8 Zeichen"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="min-h-11 text-base sm:text-sm"
                      />
                    </div>

                    {/* Nur bei der Selbstregistrierung. Wer ueber eine Einladung
                        kommt, gehoert zum einladenden Arbeitsbereich und nicht zu
                        einem werbenden Partner — dort waere das Feld eine Einladung
                        zum Missbrauch. */}
                    {mode !== "invite" && (
                      <div className="space-y-1.5">
                        <Label htmlFor="rg-ref">Empfehlungs-/Aktionscode (optional)</Label>
                        <Input
                          id="rg-ref"
                          value={referralCode}
                          onChange={(e) => setReferralCode(e.target.value)}
                          autoComplete="off"
                          placeholder="z. B. MAX-2026"
                          className="min-h-11 text-base sm:text-sm"
                        />
                        <p className="text-[11px] text-muted-foreground">
                          Über einen Partner-Link oder mit Aktionscode hier? Trag den Code ein — dann wird die Empfehlung zugeordnet.
                        </p>
                      </div>
                    )}

                    {/* ZWEI Vertragsdokumente, EINE Handlung. Der Auftragsverarbeitungsvertrag
                        kommt nach AGB § 9 Abs. 3 und nach seinem eigenen Abschnitt
                        „Abschluss" mit genau dieser Zustimmung zustande — ohne dass er
                        hier benannt und verlinkt ist, waere er nach § 312 Abs. 2 Satz 1
                        Nr. 1 BGB / § 305 Abs. 2 BGB nicht wirksam einbezogen (Betreiber
                        2026-08-16, E10). Protokolliert wird sie mit Fassungsbezeichnung
                        serverseitig (`protokolliereSuiteZustimmung`, je eine Zeile fuer
                        AGB und AVV).
                        Die Datenschutzerklaerung steht bewusst NICHT im Haken: AGB § 1
                        Abs. 4 lit. c / § 9 Abs. 1 sagen zu, dass ihr nicht zugestimmt wird
                        (Art. 13 DSGVO ist Information). Die Widerrufsbelehrung samt
                        Muster-Formular muss nach AGB § 1 Abs. 4 lit. b im Registrierungs-
                        vorgang angezeigt werden — beides steht auf /widerruf
                        (AR1-agb-04/-05, 2026-09-28). */}
                    <div className="flex items-start gap-2.5">
                      <Checkbox
                        id="rl-consent"
                        checked={consent}
                        onCheckedChange={(v) => setConsent(v === true)}
                        className="mt-0.5"
                        aria-label="AGB und Auftragsverarbeitungsvertrag zustimmen und bestätigen, mindestens 18 Jahre alt zu sein"
                      />
                      <Label
                        htmlFor="rl-consent"
                        className="text-xs font-normal leading-relaxed text-muted-foreground"
                      >
                        Ich stimme den{" "}
                        <a
                          href={centralLegalUrl("agb")}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-foreground underline underline-offset-2"
                        >
                          AGB
                        </a>{" "}
                        und dem{" "}
                        <a
                          href={centralLegalUrl("vertraege/avv")}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-foreground underline underline-offset-2"
                        >
                          Auftragsverarbeitungsvertrag
                        </a>{" "}
                        zu und bestätige, mindestens 18 Jahre alt zu sein.
                      </Label>
                    </div>
                    <p id="rl-rechtshinweise" className="text-[11px] leading-relaxed text-muted-foreground">
                      Informationen zur Verarbeitung deiner Daten:{" "}
                      <a
                        href={centralLegalUrl("datenschutz")}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-foreground underline underline-offset-2"
                      >
                        Datenschutzerklärung
                      </a>
                      . Zum Widerruf:{" "}
                      <a
                        href={centralLegalUrl("widerruf")}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-foreground underline underline-offset-2"
                      >
                        Widerrufsbelehrung und Muster-Widerrufsformular
                      </a>
                      .
                    </p>
                  </div>
                )}

                {authError && step === "register" && (
                  <p className="text-xs text-destructive">{authError}</p>
                )}

                {/* Spamschutz nur im Konto-Schritt — davor gibt es nichts zu
                    schuetzen. `zweck="registrierung"` stellt die teurere Aufgabe:
                    ein Konto ist dauerhaft, eine Anmeldung nur ein Versuch. */}
                {step === "register" && (
                  <LoginAltcha
                    onLauf={(lauf) => { altchaLauf.current = lauf; }}
                    zweck="registrierung"
                    resetSignal={altchaReset}
                  />
                )}

                <Button
                  type="submit"
                  disabled={step === "register" && (authLoading || !consent)}
                  className="min-h-11 w-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
                >
                  {step === "register"
                    ? mode === "invite"
                      ? authLoading ? "Wird eingerichtet..." : "Einrichten & loslegen"
                      : authLoading ? "Erstellen..." : "Konto erstellen"
                    : "Weiter"}
                  <ArrowRight size={16} className="ml-1.5" aria-hidden="true" />
                </Button>
              </form>
            )}
          </div>

          {/* Anbieterkennzeichnung (§ 5 Abs. 1 DDG) auf JEDER Login-Seite der
              Suite — diese Komponente ist die Eingangstuer aller Apps, nicht nur
              der Auth-App. Text und Reihenfolge absichtlich identisch zur Leiste
              der Hub-Shell (HubLauncher), damit es nur EINE Wahrheit gibt.

              Genau deshalb stehen hier auch die Pflicht-Schaltflaechen nach
              § 312k / § 356a BGB: ausgeloggte Besucher aller zwoelf Apps mit
              Checkout landen auf diesem Bildschirm — er ist die einzige Stelle,
              die alle ohne Anmeldung erreicht. */}
          <footer className="shrink-0 pt-6 sm:pt-10">
            <nav
              aria-label="Rechtliches & Status"
              className="mx-auto flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground sm:gap-x-3.5 sm:gap-y-1"
            >
              <KuendigungWiderrufLinks />
              <a href={centralLegalUrl("impressum")} className="transition-colors hover:text-foreground">Impressum</a>
              <span aria-hidden="true" className="text-muted-foreground/30">·</span>
              <a href={centralLegalUrl("datenschutz")} className="transition-colors hover:text-foreground">Datenschutz</a>
              <span aria-hidden="true" className="text-muted-foreground/30">·</span>
              <a href={centralLegalUrl("agb")} className="transition-colors hover:text-foreground">AGB</a>
              <span aria-hidden="true" className="text-muted-foreground/30">·</span>
              <a href={janewayUrl("status", "NEXT_PUBLIC_STATUS_URL")} className="transition-colors hover:text-foreground">Systemstatus</a>
            </nav>
          </footer>
          </>
        }
        rechts={<AuthShowcase workspace={name.trim() || "Dein Workspace"} WsIcon={icon.Icon} />}
      />
    </div>
  );
}

/**
 * Die Vorfuehrung fuer die rechte Spalte: Punktraster + Reel.
 *
 * **Warum sie HIER steht und nicht neben `AuthSplitScreen`:** `Reel` haengt an `SCENES`,
 * `MODES` und `typeSchedule` — dem Drehbuch in den ersten fuenfhundert Zeilen dieser
 * Datei. Ein sauberes Herausziehen hiesse, die halbe Datei mitzunehmen; das waere ein
 * eigener Umbau mit eigenem Risiko und nicht der, der hier ansteht.
 *
 * **Der Preis, bewusst bezahlt:** wer nur die Vorfuehrung braucht (die Warteseite), zieht
 * dieses Modul komplett mit. Fuer eine Seite, die eine Handvoll Wartender sieht, ist das
 * vertretbar — es gehoert aber aufgeraeumt, sobald jemand das Drehbuch ohnehin anfasst.
 *
 * Die Vorgabewerte sind fuer Aufrufer ohne Formular: die Warteseite hat kein Feld, aus
 * dem ein Arbeitsbereichsname oder ein Symbol kaeme.
 */
export function AuthShowcase({
  workspace = "Dein Workspace",
  WsIcon = Building2,
}: {
  workspace?: string;
  WsIcon?: typeof Building2;
}) {
  return (
    <>
      <AuthShowcaseBackdrop />
      <Reel workspace={workspace} WsIcon={WsIcon} />
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Der Player
// ══════════════════════════════════════════════════════════════════════

function Reel({ workspace, WsIcon }: { workspace: string; WsIcon: typeof Building2 }) {
  const [scene, setScene] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [reduced, setReduced] = useState(false);
  const elapsedRef = useRef(0);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const on = () => setReduced(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  // Deep-Link fuers Review: /ux-lab/reel?s=10 startet direkt bei Szene 10.
  // Ohne das muesste man bis zu zwei Minuten warten, um eine hintere Szene
  // ueberhaupt zu sehen.
  useEffect(() => {
    const n = Number(new URLSearchParams(window.location.search).get("s"));
    if (Number.isFinite(n) && n >= 1 && n <= SCENES.length) setScene(n - 1);
  }, []);

  const cur = SCENES[scene];

  // Die eine Uhr. Zustandswechsel passiert im rAF-Callback, NICHT im
  // setState-Updater — sonst wuerde StrictMode ihn doppelt ausfuehren und
  // Szenen ueberspringen.
  useEffect(() => {
    if (reduced) return;
    let raf = 0;
    let last = 0;
    const tick = (t: number) => {
      if (!last) last = t;
      const dt = Math.min(t - last, 64); // Tab-Rueckkehr darf nicht springen
      last = t;
      const next = elapsedRef.current + dt;
      if (next >= cur.duration) {
        elapsedRef.current = 0;
        setElapsed(0);
        setScene((s) => (s + 1) % SCENES.length);
      } else {
        elapsedRef.current = next;
        setElapsed(next);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [reduced, cur.duration]);

  // Bei reduzierter Bewegung: Endzustand zeigen, nicht animieren.
  const e = reduced ? cur.duration * 0.95 : elapsed;

  const schedule = useMemo(() => typeSchedule(cur.prompt ?? "", TYPE_MS), [cur.prompt]);
  const typedCount = schedule.filter((d) => d <= e).length;
  const thinking = e >= THINK_FROM && e < THINK_TO;

  const jump = (i: number) => {
    elapsedRef.current = 0;
    setElapsed(0);
    setScene(i);
  };

  return (
    <div
      // Drei feste Zonen: Kopf oben, Buehne dehnbar, Navigation ganz unten.
      // Dadurch bewegt sich beim Szenenwechsel NICHTS ausser der Buehne selbst.
      // Bewusst KEIN Hover-Pause mehr: das Reel soll durchlaufen, auch wenn der
      // Zeiger drueber liegt. Pausieren geht nur ueber den Knopf unten.
      className="relative flex h-[50rem] max-h-[100svh] w-full max-w-[34rem] flex-col lg:h-full lg:py-10"
    >
      {/* Kopf: der Workspace als VOLLBREITE Kopfleiste — die Zeile spannt ueber
          die ganze Spalte (Trennlinie zeigt das) und rahmt die Buehne wie ein
          App-Header, statt als kurzes Label darunter zu schweben. Der Icon-Block
          sitzt auf derselben Spalte wie die Widget-Inhalte (w-3-Spur + gap davor),
          rechts ein dezenter Status als Gegengewicht, damit die Zeile bis zum
          Rand traegt. */}
      <div className="flex shrink-0 items-center gap-2.5 border-b border-border/60 pb-3.5">
        <span className="w-3 shrink-0" aria-hidden="true" />
        <span className="flex h-8 w-8 items-center justify-center rounded-[calc(var(--radius)-1px)] bg-primary">
          <WsIcon size={16} className="text-primary-foreground" />
        </span>
        <p className="min-w-0 flex-1 truncate text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
          {workspace}
        </p>
        <span className="shrink-0 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
          Live
        </span>
      </div>


      {/* ── Bühne ───────────────────────────────────────────────────────
          Von UNTEN gestapelt: das neueste Ergebnis liegt immer auf derselben
          Hoehe, aeltere wandern nach oben aus dem Bild und werden von der
          Maske weich abgeschnitten.
          Das loest zwei Probleme auf einmal: die Spalte springt beim
          Erscheinen eines Widgets nicht mehr (vorher haben GESCHAETZTE
          Platzhalter-Hoehen alles darunter verschoben — die Schaetzung traf
          die echte Hoehe nie), und die Navigation unten steht still.
          Die linke Spur bindet die Einzelergebnisse sichtbar zu EINEM
          Vorgang zusammen, statt sie als Kartenhaufen zu zeigen. */}
      <div className="relative mt-3 min-h-0 flex-1">
        {/* Kein Ausblenden nach oben: die Buehne ist hoch genug fuer die
            laengste Szene (4 Widgets ≈ 430 px bei ~530 px Platz). Eine
            halbtransparente Karte am oberen Rand sah aus wie ein Fehler —
            fertige Schritte sollen sichtbar bleiben, nicht verblassen. */}
        <div className="absolute inset-x-0 bottom-0 flex max-h-full flex-col justify-end overflow-hidden">
          {(() => {
            const shown = cur.beats
              .map((b, i) => ({ b, i, p: clamp01((e - b.at) / b.dur) }))
              .filter((x) => x.p > 0);

            return shown.map(({ b, i, p }, k) => {
              const first = k === 0;
              const last = k === shown.length - 1;
              const spine = clamp01((e - b.at) / 650);
              return (
                <div key={`${cur.id}-${i}`} className="flex gap-2.5 pb-2 last:pb-0">
                  <div className="relative w-3 shrink-0">
                    {/* Spur: beim ersten Punkt beginnt sie, beim letzten
                        endet sie — sie laeuft nie ins Leere. */}
                    <span
                      className="absolute left-1/2 w-px -translate-x-1/2 bg-border"
                      style={{
                        top: first ? "0.9rem" : 0,
                        bottom: last ? "calc(100% - 0.9rem)" : 0,
                        opacity: spine,
                      }}
                    />
                    <span
                      className="absolute left-1/2 top-[0.9rem] h-1.5 w-1.5 rounded-full bg-foreground"
                      style={{
                        transform: `translate(-50%, -50%) scale(${0.35 + spine * 0.65})`,
                        opacity: spine,
                      }}
                    />
                  </div>
                  <div className="min-w-0 flex-1">{b.node(p)}</div>
                </div>
              );
            });
          })()}
        </div>
      </div>

      {/* Eingabe — sitzt UNTEN wie ein Chat-Composer — je nach Modus eine andere Geschichte:
          auf Anweisung = getippter Prompt · vollautomatisch = Ausloeser,
          niemand hat etwas gesagt · mit Freigabe = Prompt, aber Jane haelt
          spaeter an. Der Modus steht als Badge dran, sonst verschwimmt der
          Unterschied genau da, wo er am meisten zaehlt. */}
      <div className="mt-3 shrink-0">
        <ModeBadge mode={cur.mode} />

        {cur.mode !== "prompt" && cur.mode !== "confirm" ? (
          // Trigger-Modus: KEIN getippter Prompt, aber dieselbe Composer-Huelle
          // (rounded-2xl, bg-secondary/30) fuer einen einheitlichen Look.
          <div className="mt-2 rounded-2xl border border-border/60 bg-secondary/30 p-4">
            <div className="flex items-start gap-2.5">
              {/* Radar statt Cursor: hier tippt niemand, hier faellt etwas an. */}
              <span className="relative mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                <span
                  className="absolute inset-0 rounded-full border border-foreground/50"
                  style={{
                    transform: `scale(${0.5 + ((e / 1400) % 1) * 1.1})`,
                    opacity: 0.7 * (1 - ((e / 1400) % 1)),
                  }}
                  aria-hidden="true"
                />
                <span className="h-1.5 w-1.5 rounded-full bg-foreground" />
              </span>
              <div className="min-w-0">
                <p className="text-sm leading-relaxed">{cur.trigger!.source}</p>
                <p className="mt-0.5 font-mono text-[10px] text-muted-foreground/70">
                  {cur.trigger!.rule}
                </p>
              </div>
            </div>
          </div>
        ) : (
          // Prompt-Modus: nachempfunden dem echten ai-chat-Composer (PromptInput):
          // rounded-2xl bg-secondary/30, Text oben, Toolbar unten mit Werkzeugen
          // links und Senden-Knopf rechts, der sich fuellt, sobald fertig getippt.
          <div className="mt-2 rounded-2xl border border-border/60 bg-secondary/30">
            <div className="px-4 pt-3.5 pb-1">
              <p className="min-h-[2.5rem] text-sm leading-relaxed">
                {cur.prompt!.slice(0, typedCount)}
                {typedCount < cur.prompt!.length && !reduced && (
                  <span
                    className="ml-0.5 inline-block h-[1.05em] w-[2px] translate-y-[0.15em] bg-foreground"
                    style={{ opacity: Math.sin(e / 90) > -0.2 ? 1 : 0.12 }}
                    aria-hidden="true"
                  />
                )}
              </p>
              {thinking && (
                <div className="mt-2 h-px w-full overflow-hidden bg-border">
                  <span
                    className="block h-full bg-foreground"
                    style={{
                      width: `${clamp01((e - THINK_FROM) / (THINK_TO - THINK_FROM)) * 100}%`,
                      opacity: 0.75,
                    }}
                    aria-hidden="true"
                  />
                </div>
              )}
            </div>
            <div className="flex items-center justify-between px-2.5 pb-2.5 pt-1">
              <div className="flex items-center gap-0.5 text-muted-foreground/45">
                <span className="flex h-7 w-7 items-center justify-center rounded-md" aria-hidden="true">
                  <Paperclip size={14} />
                </span>
                <span className="flex h-7 w-7 items-center justify-center rounded-md" aria-hidden="true">
                  <Globe size={14} />
                </span>
              </div>
              {/* Senden: grau waehrend getippt wird, fuellt sich, sobald der
                  Prompt vollstaendig ist — der Moment, in dem Jane losschickt. */}
              <span
                className="flex h-7 w-7 items-center justify-center rounded-lg transition-colors duration-300"
                style={{
                  background: typedCount >= cur.prompt!.length ? "var(--primary)" : "var(--border)",
                  color: typedCount >= cur.prompt!.length ? "var(--primary-foreground)" : "var(--muted-foreground)",
                }}
                aria-hidden="true"
              >
                <ArrowUp size={15} />
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ── App-Leiste als Reise ────────────────────────────────────────
          Der Navigator IST der Suite-Teaser: jede Station eine echte
          Oekosystem-App. Zwei Ebenen pro Station: ein festes VIERECK (Icon,
          Theme-Radius) und ein RECHTECK ("Zunge") dahinter. Das Viereck
          bleibt immer stehen und faerbt sich primary, sobald die Station
          aktiv oder erledigt ist — die erledigten leuchten also als Spur.
          Die Zunge faehrt bei der aktiven Station nach rechts aus dem
          Viereck hervor (zeigt App-Name + Dauer-Fuellung) und beim Wechsel
          zeitgleich wieder dahinter zurueck. Radius = Theme, keine Pille. */}
      <div className="mt-5 shrink-0">
        <div className="relative flex items-center justify-between">
          {/* Kein durchgehender Verbinder — die Vierecke sind die Reise-Spur.
              justify-between verteilt die Stationen ueber die VOLLE Breite (so
              breit wie die Widgets darueber); die Gesamt-Kachelbreite ist
              konstant (genau eine aktive), daher bleiben die Abstaende ruhig. */}
          {SCENES.map((s, i) => {
            const active = i === scene;
            const done = i < scene;
            const lit = active || done; // aktiv ODER erledigt → Viereck primary
            const prog = active ? clamp01(e / s.duration) : done ? 1 : 0;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => jump(i)}
                aria-label={`${s.lead.app}: ${s.label}`}
                aria-current={active}
                title={`${s.lead.app} · ${s.label}`}
                data-reel-station=""
                className="group relative z-10 h-9 shrink-0"
                style={{
                  width: active ? "11rem" : "2.25rem",
                  transition: `width 520ms ${EASE}`,
                }}
              >
                {/* Rechteck = die „Zunge": liegt HINTER dem Viereck und faehrt
                    nach rechts heraus. Eigener Rahmen → wirkt ans Viereck
                    angeknuepft. Traegt die Dauer-Fuellung dieser Station. */}
                <span
                  className="absolute inset-0 overflow-hidden rounded-[var(--radius)] border border-border bg-background"
                  aria-hidden="true"
                >
                  <span
                    className="absolute inset-y-0 left-9 bg-foreground/[0.10]"
                    style={{ width: `calc((100% - 2.25rem) * ${prog})` }}
                  />
                </span>
                {/* App-Name sitzt rechts neben dem Viereck (erst sichtbar,
                    wenn die Zunge herausgefahren ist). */}
                <span
                  className="pointer-events-none absolute left-11 right-2.5 top-1/2 -translate-y-1/2 truncate text-left text-[11px] tracking-wide"
                  style={{
                    fontFamily: '"Nevan", var(--font-geist-sans), sans-serif',
                    opacity: active ? 1 : 0,
                    transition: "opacity 260ms ease",
                  }}
                >
                  {s.lead.app}
                </span>
                {/* Viereck: bleibt immer stehen, alle Rahmen sichtbar. Faerbt
                    sich primary, sobald die Station aktiv oder erledigt ist —
                    smooth ueber transition-colors. */}
                <span
                  className="absolute inset-y-0 left-0 z-10 flex w-9 items-center justify-center rounded-[var(--radius)] border transition-colors duration-500"
                  style={{
                    backgroundColor: lit ? "var(--primary)" : "var(--background)",
                    borderColor: lit ? "var(--primary)" : "var(--border)",
                  }}
                >
                  <s.lead.Icon
                    size={14}
                    className="shrink-0 transition-colors duration-500"
                    style={{ color: lit ? "var(--primary-foreground)" : "var(--muted-foreground)" }}
                  />
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
// ── Modus-Badge ────────────────────────────────────────────────────────
// Der Unterschied zwischen "Jane macht das allein" und "Jane fragt vorher"
// ist die eigentliche Vertrauensfrage. Er darf nicht implizit bleiben.

const MODES = {
  prompt: { label: "Auf Anweisung", Icon: Sparkles, note: "Du sagst was — Jane führt aus." },
  auto: { label: "Vollautomatisch", Icon: Zap, note: "Niemand hat gefragt. Eine Regel hat ausgelöst." },
  confirm: { label: "Mit Freigabe", Icon: ShieldCheck, note: "Jane bereitet alles vor — und hält an." },
  proactive: { label: "Von sich aus", Icon: MessageSquare, note: "Jane meldet sich — auch ausserhalb der Suite." },
  ask: { label: "Jane fragt nach", Icon: GraduationCap, note: "Sie merkt, was ihr fehlt — und lernt es." },
  suggest: { label: "Jane schlägt vor", Icon: Lightbulb, note: "Sie erkennt ein Muster und bietet es an." },
} as const;

function ModeBadge({ mode }: { mode: keyof typeof MODES }) {
  const m = MODES[mode];
  return (
    <div className="flex items-center gap-2">
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-medium ${
          mode === "auto"
            ? "border-foreground/45 bg-foreground/[0.07]"
            : mode === "confirm"
              ? "border-foreground/30 bg-foreground/[0.04]"
              : "border-border text-muted-foreground"
        }`}
      >
        <m.Icon size={10} strokeWidth={2.5} />
        {m.label}
      </span>
      <span className="truncate text-[11px] text-muted-foreground">{m.note}</span>
    </div>
  );
}
